import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Shield, Plus, Trash2, Building2, Users, CheckCircle,
  XCircle, AlertTriangle, RefreshCw, Eye, EyeOff,
  ChevronDown, ChevronRight, Lock, Unlock, BarChart2,
  Edit2, Save, DollarSign, Package, LogIn, FileText,
  Clock, Settings, CreditCard, Activity, Monitor,
  Palette, UserCheck, X, Download, ExternalLink, Bell, Bot, Send,
  Newspaper, Paperclip, Database, Upload, Link2, ClipboardList, Smartphone, Search,
  MapPin, History
} from 'lucide-react';

// ─── Attachment limits (mirror backend) ───────────────────────────────────────
const ATT_MAX_FILE_BYTES = 5 * 1024 * 1024;
const ATT_ACCEPT_TYPES = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.csv,.xlsx,.txt,.json';
const ATT_ALLOWED_MIME = new Set([
  'image/png','image/jpeg','image/jpg','image/gif','image/webp','application/pdf',
  'text/csv','application/csv','application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','text/plain','application/json',
]);
function readFileAsAttachment(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve({ file_name: file.name, mime_type: file.type || 'application/octet-stream', size_bytes: file.size, file_data: base64 });
    };
    r.onerror = () => reject(new Error('Read failed'));
    r.readAsDataURL(file);
  });
}
function fmtAttBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}
import { getAdminToken, setAdminToken, setUserToken } from '../contexts/AuthContext';
import AdminNewsPage from './AdminNewsPage';

// ─── Constants ────────────────────────────────────────────────────────────────
const ALL_TOOLS = [
  { path: '/ads-txt',       label: 'Ads.txt Checker',         defaultPrice: 0  },
  { path: '/sellers-json',  label: 'Sellers.json Checker',    defaultPrice: 49 },
  { path: '/crawler',       label: 'Ads.txt Crawler',         defaultPrice: 49 },
  { path: '/bundles',       label: 'Bundle Tracker',          defaultPrice: 29 },
  { path: '/bundle-lookup', label: 'Bundle Lookup',           defaultPrice: 29 },
  { path: '/schain',        label: 'Schain Validator',        defaultPrice: 49 },
  { path: '/bid-request',   label: 'Bid Request Analyzer',    defaultPrice: 49 },
  { path: '/discrepancy',   label: 'Discrepancy Checker',     defaultPrice: 49 },
  { path: '/app-category',  label: 'App Category Segmenter',  defaultPrice: 29 },
  { path: '/disc-monitor',  label: 'Discrepancy Monitor',     defaultPrice: 99 },
  { path: '/watch-tower',   label: 'Watch Tower',             defaultPrice: 99 },
];

// Only these two themes exist in App.css. Anything else falls through to
// :root (dark) AND poisons localStorage['ignitorads-theme'], which App.jsx
// then coerces back to 'dark' — silently destroying a light preference.
const THEMES = [
  { id: 'dark',  label: 'Ember Dark',  dot: '#FF6B3D', bg: '#15130F' },
  { id: 'light', label: 'Ember Light', dot: '#FF5A2C', bg: '#F7F6F3' },
];


// ─── Admin Roles ──────────────────────────────────────────────────────────────
const ADMIN_ROLES = [
  {
    id: 'admin',
    label: 'Admin',
    color: 'var(--cyan)',
    badge: 'badge-cyan',
    desc: 'Full access to all Admin Panel pages',
    pages: ['companies', 'pricing', 'engine', 'finance', 'changelog', 'admins', 'support', 'ai-support', 'news', 'ctv', 'mobileapps', 'sellersjson', 'theme'],
  },
  {
    id: 'finance',
    label: 'Finance',
    color: 'var(--green)',
    badge: 'badge-green',
    desc: 'Access to Companies, Finance, and Theme pages',
    pages: ['companies', 'finance', 'theme'],
  },
  {
    id: 'support',
    label: 'Support',
    color: 'var(--purple)',
    badge: 'badge-purple',
    desc: 'Access to Support Tickets and Theme pages',
    pages: ['support', 'theme'],
  },
];

function getRoleInfo(role) {
  return ADMIN_ROLES.find(r => r.id === role) || ADMIN_ROLES[0];
}

function canAccessPage(adminUser, pageId) {
  if (adminUser?.is_super) return true;
  const role = getRoleInfo(adminUser?.role || 'admin');
  return role.pages.includes(pageId);
}

function authH() { return { headers: { Authorization: `Bearer ${getAdminToken()}` } }; }

function fmtUSD(v) { return `$${parseFloat(v||0).toFixed(2)}`; }
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString(); }
function fmtDateTime(d) { if (!d) return '—'; return new Date(d).toLocaleString(); }

// ─── Admin login ──────────────────────────────────────────────────────────────
function AdminLogin({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post('/api/auth/admin/login', { email, password });
      setAdminToken(data.token);
      onLoggedIn(data.admin);
    } catch (err) { setError(err.response?.data?.error || 'Login failed'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ width:'100%', maxWidth:'360px' }}>
        <div style={{ textAlign:'center', marginBottom:'24px' }}>
          <img src="/brand/icon-192.png" alt="IgnitorAds" style={{ width:52, height:52, objectFit:'contain', borderRadius:'10px', marginBottom:'10px' }}/>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'20px', fontWeight:700, letterSpacing:'-0.01em', color:'var(--cyan)' }}>IgnitorAds</div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'4px', fontFamily:'var(--font-mono)', letterSpacing:'1px', textTransform:'uppercase' }}>Admin Panel</div>
        </div>
        <div className="card" style={{ padding:'24px' }}>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom:'12px' }}>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Admin Email</label>
              <input className="input-field w-full" type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus/>
            </div>
            <div style={{ marginBottom:'16px' }}>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Password</label>
              <input className="input-field w-full" type="password" value={password} onChange={e=>setPassword(e.target.value)}/>
            </div>
            {error && <div style={{ fontSize:'12px', color:'var(--red)', marginBottom:'12px' }}>{error}</div>}
            <button className="btn btn-primary w-full" type="submit" disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
              {loading ? <><span className="spinner"/> Signing in...</> : 'Sign In as Admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Company Modal (Create + Edit) ───────────────────────────────────────────
function CompanyModal({ company, toolPrices, onClose, onSaved }) {
  const isEdit = !!company;
  const [name, setName]           = useState(company?.name || '');
  const [plan, setPlan]           = useState(company?.plan || 'custom');
  const [selectedTools, setST]    = useState(company?.tools || []);
  const [price, setPrice]         = useState(company?.price_monthly || '');
  const [discountPct, setDiscount]= useState(company?.discount_pct || 0);
  const [discountReason, setDR]   = useState(company?.discount_reason || '');
  const [renewsAt, setRenews]     = useState(company?.renews_at ? company.renews_at.split('T')[0] : '');
  const [notes, setNotes]         = useState(company?.notes || '');
  const [userEmail, setUE]        = useState('');
  const [userPass, setUP]         = useState('');
  const [userName, setUN]         = useState('');
  const [saving, setSaving]       = useState(false);

  // Build full tool list from DB-sourced toolPrices — includes custom tools
  // Falls back to ALL_TOOLS if toolPrices is empty (e.g. page hasn't loaded yet)
  const allToolsList = Object.keys(toolPrices).length > 0
    ? Object.entries(toolPrices).map(([path, price]) => {
        const core = ALL_TOOLS.find(t => t.path === path);
        return { path, label: core?.label || path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), price };
      })
    : ALL_TOOLS.map(t => ({ path: t.path, label: t.label, price: t.defaultPrice }));

  const allToolPaths = allToolsList.map(t => t.path);

  // Auto-calculate price from selected tools
  const calculatedBase = plan === 'suite'
    ? allToolsList.reduce((s, t) => s + (toolPrices[t.path] || 0), 0)
    : selectedTools.reduce((s, t) => s + (toolPrices[t] || 0), 0);
  const discountAmt = calculatedBase * (parseFloat(discountPct) || 0) / 100;
  const finalPrice  = calculatedBase - discountAmt;

  function toggleTool(path) {
    setST(prev => prev.includes(path) ? prev.filter(t => t !== path) : [...prev, path]);
  }

  async function handleSave() {
    if (!name.trim()) return toast.error('Company name required');
    if (!isEdit && (!userEmail || !userPass)) return toast.error('User email and password required');
    setSaving(true);
    try {
      const payload = {
        name, plan,
        tools: plan === 'suite' ? allToolPaths : selectedTools,
        priceMonthly: price || finalPrice || null,
        discountPct: parseFloat(discountPct) || 0,
        discountReason,
        renewsAt: renewsAt || null,
        notes,
        ...(!isEdit && { userEmail, userPassword: userPass, userName }),
      };
      if (isEdit) {
        await axios.patch(`/api/admin/companies/${company.id}`, payload, authH());
        toast.success('Company updated');
      } else {
        const { data } = await axios.post('/api/admin/companies', payload, authH());
        toast.success(`Company created! Login: ${data.credentials.email}`);
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'20px', overflowY:'auto' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'680px', marginTop:'20px' }} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'15px' }}>
            {isEdit ? `Edit: ${company.name}` : 'Create New Company'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px' }}>✕</button>
        </div>

        <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:'14px', maxHeight:'75vh', overflowY:'auto' }}>
          {/* Name + Notes */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Company Name *</label>
              <input className="input-field w-full" value={name} onChange={e=>setName(e.target.value)} placeholder="Acme Media"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Internal Notes</label>
              <input className="input-field w-full" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. Referral via John"/>
            </div>
          </div>

          {/* Plan */}
          <div>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'8px' }}>Plan</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
              {[{id:'suite',label:'Full Suite',desc:`All ${allToolsList.length} tools`},{id:'custom',label:'Custom Tools',desc:'Select below'}].map(({id,label,desc}) => (
                <div key={id} onClick={()=>setPlan(id)} style={{ padding:'10px 12px', borderRadius:'var(--radius-sm)', cursor:'pointer',
                  background:plan===id?'var(--cyan-dim)':'var(--bg-elevated)',
                  border:`2px solid ${plan===id?'var(--cyan)':'var(--border)'}` }}>
                  <div style={{ fontWeight:600, fontSize:'12px', color:plan===id?'var(--cyan)':'var(--text-primary)' }}>{label}</div>
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tool selector */}
          {plan === 'custom' && (
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'8px' }}>
                Tools <span style={{ color:'var(--cyan)' }}>({selectedTools.length} selected)</span>
              </label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
                {allToolsList.map(tool => {
                  const checked = selectedTools.includes(tool.path);
                  const p = toolPrices[tool.path] ?? tool.price ?? 0;
                  return (
                    <label key={tool.path} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', borderRadius:'var(--radius-sm)', cursor:'pointer',
                      background:checked?'color-mix(in srgb, var(--cyan) 6%, transparent)':'var(--bg-elevated)',
                      border:`1px solid ${checked?'color-mix(in srgb, var(--cyan) 25%, transparent)':'var(--border)'}` }}>
                      <input type="checkbox" checked={checked} onChange={()=>toggleTool(tool.path)} style={{ accentColor:'var(--cyan)' }}/>
                      <span style={{ fontSize:'12px', flex:1 }}>{tool.label}</span>
                      <span style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>${p}/mo</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pricing */}
          <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', padding:'14px', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'11px', color:'var(--text-muted)', fontWeight:700, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Pricing</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Base (auto-calc)</label>
                <div style={{ fontSize:'16px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-mono)' }}>${calculatedBase}/mo</div>
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Discount %</label>
                <input className="input-field w-full" type="number" min="0" max="100" step="0.5"
                  value={discountPct} onChange={e=>setDiscount(e.target.value)} placeholder="0"/>
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Final Monthly</label>
                <div style={{ fontSize:'16px', fontWeight:700, color:'var(--green)', fontFamily:'var(--font-mono)' }}>${finalPrice.toFixed(2)}/mo</div>
              </div>
            </div>
            {parseFloat(discountPct) > 0 && (
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Discount Reason</label>
                <input className="input-field w-full" value={discountReason} onChange={e=>setDR(e.target.value)} placeholder="e.g. Early adopter, Partner deal, Annual commitment"/>
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'10px' }}>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Override Monthly Price</label>
                <input className="input-field w-full" type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder={`Auto: $${finalPrice.toFixed(2)}`}/>
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Renews On</label>
                <input className="input-field w-full" type="date" value={renewsAt} onChange={e=>setRenews(e.target.value)}/>
              </div>
            </div>
          </div>

          {/* Initial user — only on create */}
          {!isEdit && (
            <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'14px' }}>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', fontWeight:700, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Initial Admin User</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Full Name</label>
                  <input className="input-field w-full" value={userName} onChange={e=>setUN(e.target.value)} placeholder="Jane Smith"/>
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Work Email *</label>
                  <input className="input-field w-full" type="email" value={userEmail} onChange={e=>setUE(e.target.value)} placeholder="jane@company.com"/>
                </div>
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Password * (min 8 chars)</label>
                <input className="input-field w-full" type="password" value={userPass} onChange={e=>setUP(e.target.value)} placeholder="••••••••"/>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><Save size={13}/> {isEdit ? 'Save Changes' : 'Create Company'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Company Card ─────────────────────────────────────────────────────────────
function CompanyCard({ company, toolPrices, onRefresh }) {
  const [expanded, setExpanded]     = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [sessions, setSessions]     = useState([]);        // current — one per signed-in user
  const [sessHistory, setSessHistory] = useState([]);      // previous logins
  const [sessTotal, setSessTotal]   = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [dbUsage, setDbUsage]       = useState(null);
  const [loadingDetails, setLD]     = useState(false);
  const [suspendInput, setSuspendI] = useState(false);
  const [suspendReason, setSR]      = useState('');
  const [suspending, setSuspending] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const isSuspended = company.status === 'suspended';

  async function loadDetails() {
    if (dbUsage) return; // already loaded
    setLD(true);
    try {
      const { data } = await axios.get(`/api/admin/companies/${company.id}`, authH());
      setDbUsage(data.dbUsage || {});
      // The dedicated endpoint answers BOTH questions — who is signed in now, and
      // where they have signed in from. /companies/:id returns only the first.
      try {
        const { data: sd } = await axios.get(`/api/admin/companies/${company.id}/sessions?limit=40`, authH());
        setSessions(sd.current || []);
        setSessHistory(sd.history || []);
        setSessTotal(sd.total || 0);
      } catch {
        setSessions(data.sessions || []);   // fall back to the embedded list
      }
    } catch {} finally { setLD(false); }
  }

  function handleExpand() {
    setExpanded(e => !e);
    if (!expanded) loadDetails();
  }

  async function handleSuspend() {
    if (!suspendReason.trim()) return toast.error('Enter a suspension reason');
    setSuspending(true);
    try {
      await axios.post(`/api/admin/companies/${company.id}/suspend`, { reason: suspendReason }, authH());
      toast.success(`${company.name} suspended`);
      setSuspendI(false); setSR('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSuspending(false); }
  }

  async function handleReactivate() {
    try {
      await axios.post(`/api/admin/companies/${company.id}/reactivate`, {}, authH());
      toast.success(`${company.name} reactivated`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function handleDelete() {
    if (!window.confirm(`PERMANENTLY DELETE ${company.name} and ALL data? This CANNOT be undone.`)) return;
    try {
      await axios.delete(`/api/admin/companies/${company.id}`, authH());
      toast.success('Deleted');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function handleImpersonate() {
    if (!window.confirm(`Impersonate ${company.name}? You will be logged into their account.`)) return;
    setImpersonating(true);
    try {
      const { data } = await axios.post(`/api/admin/companies/${company.id}/impersonate`, {}, authH());
      setUserToken(data.token);
      toast.success(`Now impersonating ${company.name} — you will be redirected`);
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (err) { toast.error(err.response?.data?.error || 'Impersonation failed'); }
    finally { setImpersonating(false); }
  }

  const discount = parseFloat(company.discount_pct) || 0;
  const finalMonthly = parseFloat(company.price_monthly || 0) * (1 - discount / 100);

  return (
    <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:'8px' }}>
      {/* Row */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', cursor:'pointer' }} onClick={handleExpand}>
        {expanded ? <ChevronDown size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/> : <ChevronRight size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/>}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
            <span style={{ fontWeight:700, fontSize:'14px', color:'var(--text-primary)' }}>{company.name}</span>
            <span className={`badge ${isSuspended?'badge-red':company.status==='cancelled'?'badge-muted':'badge-green'}`} style={{ fontSize:'10px' }}>
              {company.status}
            </span>
            <span className="badge badge-muted" style={{ fontSize:'10px' }}>{company.plan === 'suite' ? 'Full Suite' : 'Custom'}</span>
            {discount > 0 && <span className="badge badge-amber" style={{ fontSize:'10px' }}>-{discount}% discount</span>}
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', display:'flex', gap:'12px' }}>
            <span>{company.user_count} user{company.user_count!=1?'s':''}</span>
            {company.renews_at && <span>Renews {fmtDate(company.renews_at)}</span>}
            {company.price_monthly && <span style={{ color:'var(--green)', fontFamily:'var(--font-mono)' }}>${finalMonthly.toFixed(2)}/mo</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:'6px', flexShrink:0 }} onClick={e=>e.stopPropagation()}>
          <button className="btn btn-secondary btn-sm" onClick={()=>setShowEdit(true)}><Edit2 size={11}/> Edit</button>
          <button className="btn btn-secondary btn-sm" onClick={handleImpersonate} disabled={impersonating}
            style={{ color:'var(--purple)', borderColor:'color-mix(in srgb, var(--purple) 30%, transparent)' }}>
            {impersonating ? <RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> : <LogIn size={11}/>}
            {' '}Impersonate
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ borderTop:'1px solid var(--border)', padding:'14px 16px', background:'var(--bg-elevated)' }}>
          {loadingDetails && <div style={{ textAlign:'center', padding:'20px' }}><span className="spinner"/></div>}

          {/* Tools */}
          <div style={{ marginBottom:'12px' }}>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>Tools</div>
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
              {company.plan === 'suite'
                ? <span className="badge badge-cyan">All 10 Tools</span>
                : (company.tools || []).map(t => {
                    const core = ALL_TOOLS.find(x => x.path === t);
                    const label = core?.label || t.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    return <span key={t} className="badge badge-muted" style={{ fontSize:'10px' }}>{label}</span>;
                  })}
            </div>
          </div>

          {/* DB Usage */}
          {dbUsage && (
            <div style={{ marginBottom:'12px' }}>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>DB Usage</div>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                {[
                  { label:'Monitor Rows', value: dbUsage.monitor_rows },
                  { label:'Reports',      value: dbUsage.report_count },
                  { label:'Bundles',      value: dbUsage.bundle_count },
                  { label:'Monitors',     value: dbUsage.monitor_count },
                  { label:'API Configs',  value: dbUsage.api_config_count },
                ].map(({ label, value }) => (
                  <div key={label} style={{ padding:'6px 10px', background:'var(--bg-card)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', textAlign:'center', minWidth:'80px' }}>
                    <div style={{ fontSize:'16px', fontWeight:700, color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>{parseInt(value||0).toLocaleString()}</div>
                    <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.4px', marginTop:'2px' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current sessions — one row per signed-in user */}
          {sessions.length > 0 && (
            <div style={{ marginBottom:'12px' }}>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
                Signed in now ({sessions.length})
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                {sessions.map(s => (
                  <div key={s.id} style={{ display:'flex', gap:'10px', alignItems:'center', padding:'6px 10px', background:'var(--bg-card)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', fontSize:'11px' }}>
                    <Monitor size={11} style={{ color:'var(--cyan)', flexShrink:0 }}/>
                    <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{s.user_email}</span>
                    <span style={{ fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>{s.ip_address}</span>
                    {(s.country || s.city) && <span style={{ color:'var(--text-muted)' }}>{[s.city, s.country].filter(Boolean).join(', ')}</span>}
                    <span style={{ color:'var(--text-muted)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.device_info?.slice(0,60)}</span>
                    {s.timezone && <span style={{ color:'var(--text-muted)', flexShrink:0 }}>{s.timezone}</span>}
                    {(s.latitude != null && s.longitude != null) && (
                      <a href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                         target="_blank" rel="noopener noreferrer"
                         title={`${s.latitude}, ${s.longitude}`}
                         style={{ color:'var(--cyan)', display:'inline-flex', alignItems:'center', flexShrink:0 }}>
                        <MapPin size={11}/>
                      </a>
                    )}
                    <span style={{ color:'var(--text-muted)', flexShrink:0 }}>Last seen {fmtDateTime(s.last_seen)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Login history — a DIFFERENT question from "who is signed in now",
              and shown separately. Merging the two is what made this panel
              previously claim hundreds of active sessions. */}
          {sessHistory.length > 0 && (
            <div style={{ marginBottom:'12px' }}>
              <div onClick={() => setShowHistory(v => !v)}
                   style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px',
                            marginBottom:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px' }}>
                {showHistory ? <ChevronDown size={11}/> : <ChevronRight size={11}/>}
                <History size={11}/> Previous logins ({sessHistory.length}{sessTotal ? ` of ${sessTotal}` : ''})
              </div>
              {showHistory && (
                <div style={{ maxHeight:'340px', overflowY:'auto', overflowX:'auto',
                              border:'1px solid var(--border)', borderRadius:'var(--radius-sm)' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
                    <thead>
                      <tr style={{ textAlign:'left', color:'var(--text-muted)', borderBottom:'1px solid var(--border)' }}>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>User</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>IP</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>City</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Region</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Country</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Lat / Long</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Timezone</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Device</th>
                        <th style={{ padding:'5px 8px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>Signed in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessHistory.map(h => (
                        <tr key={h.id} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'4px 8px', whiteSpace:'nowrap' }}>{h.user_email}</td>
                          <td style={{ padding:'4px 8px', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' }}>{h.ip_address || '—'}</td>
                          {/* A private IP and a failed lookup both land here as a
                              dash — neither is a city we simply forgot to show. */}
                          <td style={{ padding:'4px 8px' }}>{h.city || '—'}</td>
                          <td style={{ padding:'4px 8px' }}>{h.region || '—'}</td>
                          <td style={{ padding:'4px 8px' }}>{h.country || '—'}</td>
                          <td style={{ padding:'4px 8px', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' }}>
                            {(h.latitude != null && h.longitude != null)
                              ? <a href={`https://www.google.com/maps?q=${h.latitude},${h.longitude}`}
                                   target="_blank" rel="noopener noreferrer" style={{ color:'var(--cyan)' }}>
                                  {Number(h.latitude).toFixed(3)}, {Number(h.longitude).toFixed(3)}
                                </a>
                              : '—'}
                          </td>
                          <td style={{ padding:'4px 8px', whiteSpace:'nowrap' }}>{h.timezone || '—'}</td>
                          <td style={{ padding:'4px 8px', maxWidth:'220px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                              title={h.device_info || ''}>{h.device_info?.slice(0,70) || '—'}</td>
                          <td style={{ padding:'4px 8px', whiteSpace:'nowrap', color:'var(--text-muted)' }}>{fmtDateTime(h.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {sessions.length === 0 && !loadingDetails && (
            <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'12px' }}>Nobody signed in right now</div>
          )}

          {/* Suspend / Reactivate / Delete */}
          {isSuspended && (
            <div className="alert alert-warning" style={{ marginBottom:'10px' }}>
              <AlertTriangle size={12}/> Suspended: {company.suspend_reason || 'No reason given'} — all data preserved
            </div>
          )}
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {isSuspended
              ? <button className="btn btn-primary btn-sm" onClick={handleReactivate}><Unlock size={11}/> Reactivate</button>
              : !suspendInput
                ? <button className="btn btn-secondary btn-sm" onClick={()=>setSuspendI(true)} style={{ color:'var(--amber)', borderColor:'color-mix(in srgb, var(--amber) 30%, transparent)' }}><Lock size={11}/> Suspend</button>
                : <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                    <input className="input-field" style={{ width:'220px' }} placeholder="Reason e.g. Non-payment" value={suspendReason} onChange={e=>setSR(e.target.value)}/>
                    <button className="btn btn-sm" style={{ background:'color-mix(in srgb, var(--amber) 15%, transparent)', color:'var(--amber)', border:'1px solid color-mix(in srgb, var(--amber) 30%, transparent)' }} onClick={handleSuspend} disabled={suspending}>
                      {suspending ? <RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> : 'Confirm'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={()=>{setSuspendI(false);setSR('');}}>Cancel</button>
                  </div>}
            <button className="btn btn-danger btn-sm" onClick={handleDelete} style={{ marginLeft:'auto' }}><Trash2 size={11}/> Delete Permanently</button>
          </div>
        </div>
      )}

      {showEdit && (
        <CompanyModal company={company} toolPrices={toolPrices} onClose={()=>setShowEdit(false)} onSaved={onRefresh}/>
      )}
    </div>
  );
}

// ─── Tool Pricing Page ────────────────────────────────────────────────────────
// Period definitions — months and default discount % for each tier.
// Discount % applies to the period total: price = monthly × months × (1 − pct/100).
const PRICING_PERIODS = [
  { key:'quarterly',  pctField:'quarterly_discount_pct',    months:3,  label:'Quarterly',   suffix:'/q',    defaultPct:5  },
  { key:'semiAnnual', pctField:'semi_annual_discount_pct',  months:6,  label:'Semi-Annual', suffix:'/6mo',  defaultPct:10 },
  { key:'annual',     pctField:'annual_discount_pct',       months:12, label:'Annual',      suffix:'/yr',   defaultPct:15 },
  { key:'biennial',   pctField:'biennial_discount_pct',     months:24, label:'2-Year',      suffix:'/2yr',  defaultPct:25 },
];

// Normalise a DB row (or seed row) into the shape ToolPricingPage uses internally.
function normalizePricingRow(row) {
  const num = (v, fallback) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    tool_path: row.tool_path,
    label:     row.label,
    price_usd: num(row.price_usd, 0),
    quarterly_discount_pct:   num(row.quarterly_discount_pct,   5),
    semi_annual_discount_pct: num(row.semi_annual_discount_pct, 10),
    annual_discount_pct:      num(row.annual_discount_pct,      15),
    biennial_discount_pct:    num(row.biennial_discount_pct,    25),
  };
}

// Period price = monthly × months × (1 − pct/100). Clamped & defensive.
function calcPeriodPrice(monthly, months, pct) {
  const m = parseFloat(monthly) || 0;
  const p = Math.max(0, Math.min(100, parseFloat(pct) || 0));
  return m * months * (1 - p / 100);
}

// ─── Engine Settings (per-tool POLICY override panel) ─────────────────────────
function KnobRow({ knobKey, knob, value, overridden, onChange, onReset }) {
  const isGeo = knob.type === 'geo';
  const isCap = knob.type === 'cap';
  const isBool = knob.type === 'bool';
  const [geoText, setGeoText] = useState(isGeo && Array.isArray(value) ? value.join(', ') : '');
  useEffect(() => { if (isGeo) setGeoText(Array.isArray(value) ? value.join(', ') : ''); }, [value, isGeo]);

  function commitGeo(text) {
    const seen = new Set(); const out = [];
    text.split(',').map(x => x.trim().toLowerCase()).forEach(c => {
      if (/^[a-z]{2}$/.test(c) && !seen.has(c)) { seen.add(c); out.push(c); }
    });
    onChange(out);
  }

  const defaultLabel = isGeo
    ? (Array.isArray(knob.default) ? knob.default.join(', ') : '—')
    : isBool ? (knob.default ? 'on' : 'off')
    : (isCap && (knob.default === null || knob.default === undefined) ? 'uncapped' : String(knob.default));
  const rangeHint = (isGeo || isBool) ? '' : (isCap ? `0 = uncapped · ${knob.min}–${knob.max}` : `${knob.min}–${knob.max}`);
  const numEmpty = (value === '' || value === null || value === undefined || (isCap && value === 0));

  return (
    <div style={{ display:'grid', gridTemplateColumns:'minmax(210px, 1.3fr) 2fr auto', gap:'12px', alignItems:'start',
                  padding:'10px 12px', border:'1px solid var(--border)', borderRadius:'8px',
                  background: overridden ? 'color-mix(in srgb, var(--cyan) 5%, transparent)' : 'transparent' }}>
      <div>
        <div style={{ fontSize:'12px', fontWeight:600, display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
          {knob.label}
          {overridden && <span style={{ fontSize:'9px', color:'var(--cyan)', border:'1px solid var(--cyan)', borderRadius:'4px', padding:'1px 4px', letterSpacing:'0.3px' }}>OVERRIDE</span>}
        </div>
        <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)', marginTop:'3px' }}>
          {knobKey}{rangeHint ? ` · ${rangeHint}` : ''} · default {defaultLabel}
        </div>
      </div>

      <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.5 }}>
        {knob.desc}
        {isGeo && (
          <input className="input-field w-full" style={{ marginTop:'6px', fontSize:'11px' }}
            value={geoText}
            onChange={e => setGeoText(e.target.value)}
            onBlur={e => commitGeo(e.target.value)}
            placeholder="us, de, jp …" />
        )}
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:'8px', justifyContent:'flex-end' }}>
        {isBool && (
          <label style={{ display:'inline-flex', alignItems:'center', gap:'6px', fontSize:'12px', cursor:'pointer', userSelect:'none' }}>
            <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} style={{ width:'15px', height:'15px', cursor:'pointer', accentColor:'var(--cyan)' }} />
            {value ? 'On' : 'Off'}
          </label>
        )}
        {!isGeo && !isBool && (
          <input className="input-field" type="number"
            min={isCap ? 0 : knob.min} max={knob.max} step="1"
            style={{ width:'92px', fontSize:'12px', textAlign:'center' }}
            value={numEmpty ? '' : value}
            onChange={e => { const r = e.target.value; onChange(r === '' ? '' : Number(r)); }}
            placeholder={isCap ? '∞' : ''} />
        )}
        <button className="btn btn-secondary" title="Reset to default"
          style={{ fontSize:'11px', padding:'4px 8px', visibility: overridden ? 'visible' : 'hidden' }}
          onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

function RunCrawlCard() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const isStaging = typeof window !== 'undefined' && /staging/i.test(window.location.hostname);
  const env = isStaging ? 'STAGING' : 'PRODUCTION';
  async function run() {
    if (busy) return;
    if (!window.confirm(`Run the daily Bundle Tracker crawl now on ${env}? It processes all approved lists and can take several minutes.`)) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post('/api/admin/run-daily-crawl', {}, authH());
      setMsg(data?.message || 'Crawl started \u2014 watch the [DailyCrawl] logs.');
    } catch (e) {
      setMsg(e.response?.data?.error || 'Failed to start crawl.');
    } finally { setBusy(false); }
  }
  return (
    <div className="card" style={{ padding:'14px', marginBottom:'16px', borderLeft:`3px solid ${isStaging ? 'var(--amber)' : 'var(--cyan)'}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
        <div>
          <div style={{ fontWeight:700, fontSize:'13px' }}>Run Daily Crawl Now</div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
            Triggers the app-ads.txt crawl on <strong>{env}</strong> (this environment). Fire-and-forget \u2014 watch the <code>[DailyCrawl]</code> logs.
          </div>
        </div>
        <button className="btn btn-primary" style={{ fontSize:'12px', whiteSpace:'nowrap' }} onClick={run} disabled={busy}>
          {busy ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Starting\u2026</> : <>\u25b6 Run Crawl \u2014 {env}</>}
        </button>
      </div>
      {msg && <div style={{ marginTop:'10px', fontSize:'11px', color:'var(--text-secondary)' }}>{msg}</div>}
    </div>
  );
}

function EngineSettingsPage() {
  const [schema, setSchema]         = useState(null);
  const [overrides, setOverrides]   = useState({});  // saved (server) state
  const [draft, setDraft]           = useState({});  // editable working copy
  const [loading, setLoading]       = useState(true);
  const [savingTool, setSavingTool] = useState(null);
  const [activeTool, setActiveTool] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/tool-policy', authH());
      const sc = data.schema || {};
      const ov = data.overrides || {};
      setSchema(sc);
      setOverrides(ov);
      setDraft(JSON.parse(JSON.stringify(ov)));
      setActiveTool(t => (t && sc[t]) ? t : (Object.keys(sc)[0] || null));
    } catch { toast.error('Failed to load engine settings'); }
    finally { setLoading(false); }
  }

  function effective(tp, key, knob) {
    const ov = draft[tp] || {};
    return (key in ov) ? ov[key] : knob.default;
  }
  function isOverridden(tp, key) { return key in (draft[tp] || {}); }
  function setKnob(tp, key, val) {
    setDraft(prev => ({ ...prev, [tp]: { ...(prev[tp] || {}), [key]: val } }));
  }
  function resetKnob(tp, key) {
    setDraft(prev => { const t = { ...(prev[tp] || {}) }; delete t[key]; return { ...prev, [tp]: t }; });
  }
  function resetAllForTool(tp) { setDraft(prev => ({ ...prev, [tp]: {} })); }
  function toolDirty(tp) { return JSON.stringify(draft[tp] || {}) !== JSON.stringify(overrides[tp] || {}); }
  function countOverrides(tp) { return Object.keys(draft[tp] || {}).length; }

  async function saveTool(tp) {
    setSavingTool(tp);
    try {
      const { data } = await axios.patch('/api/admin/tool-policy',
        { tool_path: tp, overrides: draft[tp] || {} }, authH());
      const clean = data.overrides || {};
      setOverrides(prev => ({ ...prev, [tp]: clean }));
      setDraft(prev => ({ ...prev, [tp]: JSON.parse(JSON.stringify(clean)) }));
      toast.success(`${schema[tp].label} settings applied live`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSavingTool(null); }
  }

  if (loading) return <div style={{ padding:'20px', color:'var(--text-muted)', fontSize:'13px' }}><RefreshCw size={13} style={{ animation:'spin 1s linear infinite', verticalAlign:'-2px' }}/> Loading engine settings…</div>;
  if (!schema) return null;

  const tools = Object.keys(schema);
  const tool = (activeTool && schema[activeTool]) ? activeTool : tools[0];
  const toolSchema = schema[tool];

  return (
    <div>
      <div style={{ marginBottom:'16px' }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'4px' }}>Engine Settings</h2>
        <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>
          Tune each tool's crawl / resolution engine at runtime. Values are validated against safe limits, saved per tool, and applied live without a redeploy. Leave a knob untouched to keep its built-in default; use <strong>Reset</strong> to drop an override.
        </div>
      </div>

      <RunCrawlCard />

      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'16px' }}>
        {tools.map(tp => (
          <button key={tp} onClick={() => setActiveTool(tp)}
            className={tp === tool ? 'btn btn-primary' : 'btn btn-secondary'}
            style={{ fontSize:'12px' }}>
            {schema[tp].label}
            {countOverrides(tp) > 0 && (
              <span style={{ marginLeft:'6px', fontSize:'10px', fontWeight:700, color: tp === tool ? 'var(--on-accent)' : 'var(--cyan)' }}>
                ●{countOverrides(tp)}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px', flexWrap:'wrap', gap:'8px' }}>
          <div style={{ fontWeight:700, fontSize:'13px' }}>
            {toolSchema.label} <span style={{ color:'var(--text-muted)', fontFamily:'var(--font-mono)', fontSize:'11px', fontWeight:400 }}>{tool}</span>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn btn-secondary" style={{ fontSize:'12px' }}
              onClick={() => resetAllForTool(tool)} disabled={countOverrides(tool) === 0}>Reset all</button>
            <button className="btn btn-primary" style={{ fontSize:'12px' }}
              onClick={() => saveTool(tool)} disabled={savingTool === tool || !toolDirty(tool)}>
              {savingTool === tool
                ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Saving…</>
                : <><Save size={13}/> Save {toolSchema.label}</>}
            </button>
          </div>
        </div>

        <div style={{ display:'grid', gap:'10px' }}>
          {Object.entries(toolSchema.knobs).map(([key, knob]) => (
            <KnobRow key={key} knobKey={key} knob={knob}
              value={effective(tool, key, knob)}
              overridden={isOverridden(tool, key)}
              onChange={(v) => setKnob(tool, key, v)}
              onReset={() => resetKnob(tool, key)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ToolPricingPage() {
  const [prices, setPrices]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newTool, setNewTool] = useState({ tool_path: '', label: '', price_usd: '' });

  // Core tool paths that cannot be removed
  const CORE_PATHS = new Set(ALL_TOOLS.map(t => t.path));

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/tool-pricing', authH());
      // Start with DB rows (which include any custom tools + core tools)
      const dbMap = {};
      data.forEach(row => { dbMap[row.tool_path] = normalizePricingRow(row); });
      // Ensure all core tools are always shown, even if missing from DB
      const merged = [
        ...ALL_TOOLS.map(t =>
          dbMap[t.path] || normalizePricingRow({ tool_path: t.path, label: t.label, price_usd: t.defaultPrice })
        ),
        // Custom tools from DB that aren't in ALL_TOOLS
        ...data
          .filter(row => !CORE_PATHS.has(row.tool_path))
          .map(normalizePricingRow),
      ];
      setPrices(merged);
    } catch { toast.error('Failed to load pricing'); }
    finally { setLoading(false); }
  }

  function updateField(path, field, val) {
    setPrices(prev => prev.map(p => {
      if (p.tool_path !== path) return p;
      if (field === 'label') return { ...p, label: val };
      // Numeric fields
      let num = parseFloat(val);
      if (!Number.isFinite(num)) num = 0;
      if (field.endsWith('_pct')) num = Math.max(0, Math.min(100, num));
      else num = Math.max(0, num);
      return { ...p, [field]: num };
    }));
  }

  function removeTool(path) {
    if (CORE_PATHS.has(path)) return toast.error('Core tools cannot be removed');
    if (!window.confirm(`Remove "${path}" from tool pricing?`)) return;
    setPrices(prev => prev.filter(p => p.tool_path !== path));
  }

  function addTool() {
    const path = newTool.tool_path.trim();
    if (!path) return toast.error('Tool path is required (e.g. /my-tool)');
    if (!path.startsWith('/')) return toast.error('Path must start with / (e.g. /my-tool)');
    if (prices.find(p => p.tool_path === path)) return toast.error('A tool with this path already exists');
    if (!newTool.label.trim()) return toast.error('Tool label is required');
    setPrices(prev => [
      ...prev,
      normalizePricingRow({
        tool_path: path,
        label: newTool.label.trim(),
        price_usd: parseFloat(newTool.price_usd) || 0,
        // discount columns fall back to defaults in normalizePricingRow
      }),
    ]);
    setNewTool({ tool_path: '', label: '', price_usd: '' });
    setShowAdd(false);
    toast.success('Tool added — click Save Pricing to persist');
  }

  async function handleSave() {
    setSaving(true);
    try {
      await axios.patch('/api/admin/tool-pricing', { prices }, authH());
      toast.success('Pricing saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }

  const monthlyTotal = prices.reduce((s, p) => s + (p.price_usd || 0), 0);

  // Shared cell styles
  const pctInputStyle  = { width:'58px', fontSize:'11px', padding:'4px 6px', textAlign:'center' };
  const subThStyle     = { fontSize:'10px', textAlign:'center', fontWeight:600, color:'var(--text-muted)', letterSpacing:'0.4px', textTransform:'uppercase' };
  const groupThStyle   = { textAlign:'center', borderLeft:'1px solid var(--border)', fontSize:'11px', fontWeight:700, letterSpacing:'0.5px' };
  const groupCellLeftBorder = { borderLeft:'1px solid var(--border)' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'4px' }}>Tool Pricing</h2>
          <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>
            Set the monthly price per tool. Each commitment tier (Quarterly / Semi-Annual / Annual / 2-Year) takes an editable discount %, and the Price column auto-calculates as <span style={{ fontFamily:'var(--font-mono)' }}>Monthly × months × (1 − %)</span>.
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-secondary" onClick={() => setShowAdd(s => !s)}><Plus size={13}/> New Tool</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Saving...</> : <><Save size={13}/> Save Pricing</>}
          </button>
        </div>
      </div>

      {/* Add New Tool form */}
      {showAdd && (
        <div className="card" style={{ marginBottom:'16px', padding:'16px' }}>
          <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'12px' }}>Add New Tool</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 160px auto', gap:'10px', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Path * (e.g. /my-tool)</label>
              <input className="input-field w-full" value={newTool.tool_path}
                onChange={e => setNewTool(p => ({ ...p, tool_path: e.target.value }))}
                placeholder="/fetch-engine"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Label *</label>
              <input className="input-field w-full" value={newTool.label}
                onChange={e => setNewTool(p => ({ ...p, label: e.target.value }))}
                placeholder="Fetch Engine"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Price (USD/mo)</label>
              <input className="input-field w-full" type="number" min="0" step="1" value={newTool.price_usd}
                onChange={e => setNewTool(p => ({ ...p, price_usd: e.target.value }))}
                placeholder="49"/>
            </div>
            <div style={{ display:'flex', gap:'6px' }}>
              <button className="btn btn-primary btn-sm" onClick={addTool}><Plus size={11}/> Add</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>✕</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {/* Inner scroll wrapper so the table can extend wider than the card on small screens */}
          <div style={{ overflowX:'auto' }}>
            <table className="data-table" style={{ minWidth:'1500px' }}>
              <thead>
                <tr>
                  <th rowSpan={2}>Tool</th>
                  <th rowSpan={2}>Path</th>
                  <th rowSpan={2} style={{ width:'160px', textAlign:'center' }}>Monthly Price (USD)</th>
                  {PRICING_PERIODS.map(period => (
                    <th key={period.key} colSpan={2} style={groupThStyle}>{period.label}</th>
                  ))}
                  <th rowSpan={2} style={{ width:'80px', borderLeft:'1px solid var(--border)' }}>Tier</th>
                  <th rowSpan={2} style={{ width:'50px' }}></th>
                </tr>
                <tr>
                  {PRICING_PERIODS.map(period => (
                    <React.Fragment key={period.key}>
                      <th style={{ ...subThStyle, width:'70px', ...groupCellLeftBorder }}>Disc %</th>
                      <th style={{ ...subThStyle, width:'100px' }}>Price</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prices.map(p => {
                  const isCore = CORE_PATHS.has(p.tool_path);
                  return (
                    <tr key={p.tool_path}>
                      <td>
                        {isCore
                          ? <span style={{ fontWeight:600 }}>{p.label}</span>
                          : <input className="input-field" style={{ fontSize:'12px', padding:'4px 8px', width:'100%' }}
                              value={p.label} onChange={e => updateField(p.tool_path, 'label', e.target.value)}/>
                        }
                      </td>
                      <td style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--text-muted)' }}>{p.tool_path}</td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', justifyContent:'flex-start' }}>
                          <span style={{ color:'var(--text-muted)' }}>$</span>
                          <input className="input-field" type="number" min="0" step="1" style={{ width:'90px' }}
                            value={p.price_usd} onChange={e => updateField(p.tool_path, 'price_usd', e.target.value)}/>
                          <span style={{ color:'var(--text-muted)', fontSize:'11px' }}>/mo</span>
                        </div>
                      </td>
                      {PRICING_PERIODS.map(period => {
                        const pct = p[period.pctField];
                        const periodPrice = calcPeriodPrice(p.price_usd, period.months, pct);
                        return (
                          <React.Fragment key={period.key}>
                            <td style={groupCellLeftBorder}>
                              <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent:'center' }}>
                                <input className="input-field" type="number" min="0" max="100" step="0.5"
                                  style={pctInputStyle}
                                  value={pct}
                                  onChange={e => updateField(p.tool_path, period.pctField, e.target.value)}/>
                                <span style={{ color:'var(--text-muted)', fontSize:'10px' }}>%</span>
                              </div>
                            </td>
                            <td style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                              <span style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color: p.price_usd === 0 ? 'var(--text-muted)' : 'var(--green)' }}>
                                ${periodPrice.toFixed(2)}
                              </span>
                              <span style={{ color:'var(--text-muted)', fontSize:'10px', marginLeft:'2px' }}>{period.suffix}</span>
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td style={groupCellLeftBorder}>
                        <span className={`badge ${p.price_usd === 0 ? 'badge-green' : 'badge-purple'}`} style={{ fontSize:'10px' }}>
                          {p.price_usd === 0 ? 'Free' : 'Pro'}
                        </span>
                        {!isCore && <span className="badge badge-amber" style={{ fontSize:'9px', marginLeft:'4px' }}>Custom</span>}
                      </td>
                      <td>
                        {!isCore && (
                          <button className="btn btn-danger btn-sm" style={{ padding:'2px 6px' }} onClick={() => removeTool(p.tool_path)}>
                            <Trash2 size={10}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bg-elevated)' }}>
                  <td colSpan={2} style={{ fontWeight:700 }}>Full Suite ({prices.length} tools)</td>
                  <td style={{ fontWeight:700, color:'var(--green)', fontFamily:'var(--font-mono)', textAlign:'center' }}>${monthlyTotal.toFixed(2)}/mo</td>
                  {PRICING_PERIODS.map(period => {
                    const periodSuiteTotal = prices.reduce(
                      (s, p) => s + calcPeriodPrice(p.price_usd, period.months, p[period.pctField]),
                      0
                    );
                    return (
                      <React.Fragment key={period.key}>
                        <td style={groupCellLeftBorder}></td>
                        <td style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                          <span style={{ fontWeight:700, color:'var(--green)', fontFamily:'var(--font-mono)', fontSize:'11px' }}>
                            ${periodSuiteTotal.toFixed(2)}
                          </span>
                          <span style={{ color:'var(--text-muted)', fontSize:'10px', marginLeft:'2px' }}>{period.suffix}</span>
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td colSpan={2} style={groupCellLeftBorder}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Finance Page ─────────────────────────────────────────────────────────────
// ─── Status label helpers ────────────────────────────────────────────────────
const STATUS_LABEL = {
  pending:  'Yet to Receive',
  paid:     'Received',
  overdue:  'Overdue',
  cancelled:'Archived',
  archived: 'Archived',
};
const STATUS_BADGE = {
  pending:  'badge-amber',
  paid:     'badge-green',
  overdue:  'badge-red',
  cancelled:'badge-muted',
  archived: 'badge-muted',
};

// ─── Invoice Edit Modal ───────────────────────────────────────────────────────
function InvoiceEditModal({ inv, companies, onClose, onSaved }) {
  const [amount,      setAmount]  = useState(String(inv.amount_usd || ''));
  const [discount,    setDiscount]= useState(String(inv.discount_pct || 0));
  const [dueDate,     setDueDate] = useState(inv.due_date ? inv.due_date.split('T')[0] : '');
  const [description, setDesc]    = useState(inv.description || '');
  const [notes,       setNotes]   = useState(inv.notes || '');
  const [saving,      setSaving]  = useState(false);

  const base  = parseFloat(amount) || 0;
  const disc  = parseFloat(discount) || 0;
  const final = base * (1 - disc / 100);

  async function handleSave() {
    setSaving(true);
    try {
      await axios.patch(`/api/admin/invoices/${inv.id}`, {
        amountUsd:   base,
        discountPct: disc,
        dueDate:     dueDate || null,
        description,
        notes,
      }, authH());
      toast.success('Invoice updated');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'500px' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontWeight:700, fontSize:'14px' }}>Edit Invoice — {inv.invoice_number}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'18px' }}>✕</button>
        </div>
        <div style={{ padding:'18px', display:'flex', flexDirection:'column', gap:'12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Amount (USD)</label>
              <input className="input-field w-full" type="number" value={amount} onChange={e=>setAmount(e.target.value)}/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Discount %</label>
              <input className="input-field w-full" type="number" min="0" max="100" value={discount} onChange={e=>setDiscount(e.target.value)}/>
            </div>
          </div>
          <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:'12px', color:'var(--text-muted)' }}>Final amount</span>
            <span style={{ fontSize:'16px', fontWeight:700, color:'var(--green)', fontFamily:'var(--font-mono)' }}>${final.toFixed(2)}</span>
          </div>
          <div>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Due Date</label>
            <input className="input-field w-full" type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}/>
          </div>
          <div>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Description (shown in list)</label>
            <input className="input-field w-full" value={description} onChange={e=>setDesc(e.target.value)} placeholder="e.g. March 2026 subscription"/>
          </div>
          <div>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Internal Notes</label>
            <input className="input-field w-full" value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>
        </div>
        <div style={{ padding:'12px 18px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><Save size={12}/> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice Archive Modal ────────────────────────────────────────────────────
function InvoiceArchiveModal({ inv, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleArchive() {
    if (!reason.trim()) return toast.error('Please enter a reason for archiving');
    setSaving(true);
    try {
      await axios.patch(`/api/admin/invoices/${inv.id}`, {
        status: 'archived',
        archiveReason: reason,
      }, authH());
      toast.success('Invoice archived');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid color-mix(in srgb, var(--red) 30%, transparent)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'420px' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontWeight:700, fontSize:'14px', color:'var(--amber)' }}>Archive Invoice — {inv.invoice_number}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'18px' }}>✕</button>
        </div>
        <div style={{ padding:'18px', display:'flex', flexDirection:'column', gap:'12px' }}>
          <div className="alert alert-warning" style={{ fontSize:'12px' }}>
            <AlertTriangle size={12}/> This invoice will be archived and hidden from active views. It will not be deleted and can be viewed under the Archived filter.
          </div>
          <div>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Reason for archiving *</label>
            <input className="input-field w-full" value={reason} onChange={e=>setReason(e.target.value)}
              placeholder="e.g. Duplicate invoice, Client cancelled, Replaced by INV-XXXXX" autoFocus/>
          </div>
        </div>
        <div style={{ padding:'12px 18px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm" onClick={handleArchive} disabled={saving}
            style={{ background:'color-mix(in srgb, var(--amber) 15%, transparent)', color:'var(--amber)', border:'1px solid color-mix(in srgb, var(--amber) 40%, transparent)' }}>
            {saving ? 'Archiving...' : 'Archive Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── File upload helper ───────────────────────────────────────────────────────
function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// ─── Invoice Row (used inside month groups) ───────────────────────────────────
function InvoiceRow({ inv, onRefresh }) {
  const [showEdit,    setShowEdit]    = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [uploading,   setUploading]   = useState(''); // 'invoice' | 'pop' | ''

  async function handleFileUpload(type, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('File must be under 5MB');
    setUploading(type);
    try {
      const b64 = await toBase64(file);
      const payload = type === 'invoice'
        ? { invoiceFile: b64, invoiceFileName: file.name }
        : { popFile: b64, popFileName: file.name };
      await axios.patch(`/api/admin/invoices/${inv.id}`, payload, authH());
      toast.success(`${type === 'invoice' ? 'Invoice' : 'Proof of Payment'} uploaded`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(''); }
  }

  async function markReceived() {
    try {
      await axios.post(`/api/admin/invoices/${inv.id}/record-payment`, { amountUsd: inv.final_amount }, authH());
      toast.success('Marked as Received');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function markOverdue() {
    try {
      await axios.patch(`/api/admin/invoices/${inv.id}`, { status:'overdue' }, authH());
      toast.success('Marked as Overdue');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  const isArchived = inv.status === 'archived' || inv.status === 'cancelled';

  return (
    <tr key={inv.id} style={{ opacity: isArchived ? 0.55 : 1 }}>
      {/* Invoice # */}
      <td style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--cyan)' }}>{inv.invoice_number}</td>
      {/* Company */}
      <td style={{ fontWeight:600, fontSize:'12px' }}>{inv.company_name}</td>
      {/* Description */}
      <td style={{ fontSize:'11px', color:'var(--text-muted)', maxWidth:'160px' }}>
        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.description || '—'}</div>
      </td>
      {/* Amount */}
      <td style={{ fontFamily:'var(--font-mono)', fontSize:'12px' }}>{fmtUSD(inv.amount_usd)}</td>
      {/* Discount */}
      <td style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--amber)' }}>
        {parseFloat(inv.discount_pct||0)>0 ? `-${inv.discount_pct}%` : '—'}
      </td>
      {/* Final */}
      <td style={{ fontFamily:'var(--font-mono)', color:'var(--green)', fontWeight:700, fontSize:'12px' }}>{fmtUSD(inv.final_amount)}</td>
      {/* Status */}
      <td>
        <span className={`badge ${STATUS_BADGE[inv.status] || 'badge-muted'}`} style={{ fontSize:'10px', whiteSpace:'nowrap' }}>
          {STATUS_LABEL[inv.status] || inv.status}
        </span>
      </td>
      {/* Due */}
      <td style={{ fontSize:'11px', color: inv.due_date && new Date(inv.due_date)<new Date() && inv.status==='pending' ? 'var(--red)' : 'var(--text-muted)', whiteSpace:'nowrap' }}>
        {fmtDate(inv.due_date)}
      </td>
      {/* Received on */}
      <td style={{ fontSize:'11px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{fmtDate(inv.paid_at)}</td>
      {/* Actions */}
      <td>
        <div style={{ display:'flex', gap:'4px', alignItems:'center', flexWrap:'nowrap' }}>
          {/* Status transitions */}
          {inv.status === 'pending' && <>
            <button className="btn btn-sm" style={{ fontSize:'10px', padding:'2px 7px', color:'var(--green)', border:'1px solid color-mix(in srgb, var(--green) 30%, transparent)', background:'color-mix(in srgb, var(--green) 8%, transparent)', whiteSpace:'nowrap' }} onClick={markReceived}>
              <CheckCircle size={9}/> Received
            </button>
            <button className="btn btn-sm" style={{ fontSize:'10px', padding:'2px 7px', color:'var(--red)', border:'1px solid color-mix(in srgb, var(--red) 30%, transparent)', background:'color-mix(in srgb, var(--red) 8%, transparent)', whiteSpace:'nowrap' }} onClick={markOverdue}>
              Overdue
            </button>
          </>}
          {inv.status === 'overdue' && (
            <button className="btn btn-sm" style={{ fontSize:'10px', padding:'2px 7px', color:'var(--green)', border:'1px solid color-mix(in srgb, var(--green) 30%, transparent)', background:'color-mix(in srgb, var(--green) 8%, transparent)', whiteSpace:'nowrap' }} onClick={markReceived}>
              <CheckCircle size={9}/> Received
            </button>
          )}

          {/* Edit */}
          {!isArchived && (
            <button className="btn btn-sm" style={{ fontSize:'10px', padding:'2px 7px', color:'var(--cyan)', border:'1px solid color-mix(in srgb, var(--cyan) 30%, transparent)', background:'color-mix(in srgb, var(--cyan) 6%, transparent)', whiteSpace:'nowrap' }} onClick={()=>setShowEdit(true)}>
              <Edit2 size={9}/> Edit
            </button>
          )}

          {/* Upload Invoice */}
          <label title="Upload Invoice document" style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'3px', fontSize:'10px', padding:'2px 7px', color: inv.invoice_file_name ? 'var(--green)' : 'var(--text-muted)', border:`1px solid ${inv.invoice_file_name?'color-mix(in srgb, var(--green) 30%, transparent)':'var(--border)'}`, background: inv.invoice_file_name?'color-mix(in srgb, var(--green) 6%, transparent)':'var(--bg-elevated)', borderRadius:'4px', whiteSpace:'nowrap' }}>
            {uploading === 'invoice' ? <RefreshCw size={9} style={{ animation:'spin 1s linear infinite' }}/> : <FileText size={9}/>}
            {inv.invoice_file_name ? 'INV ✓' : 'INV'}
            <input type="file" style={{ display:'none' }} accept=".pdf,.png,.jpg,.jpeg" onChange={e=>handleFileUpload('invoice', e.target.files?.[0])}/>
          </label>

          {/* Upload POP */}
          <label title="Upload Proof of Payment / bank screenshot" style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'3px', fontSize:'10px', padding:'2px 7px', color: inv.pop_file_name ? 'var(--green)' : 'var(--text-muted)', border:`1px solid ${inv.pop_file_name?'color-mix(in srgb, var(--green) 30%, transparent)':'var(--border)'}`, background: inv.pop_file_name?'color-mix(in srgb, var(--green) 6%, transparent)':'var(--bg-elevated)', borderRadius:'4px', whiteSpace:'nowrap' }}>
            {uploading === 'pop' ? <RefreshCw size={9} style={{ animation:'spin 1s linear infinite' }}/> : <Download size={9}/>}
            {inv.pop_file_name ? 'POP ✓' : 'POP'}
            <input type="file" style={{ display:'none' }} accept=".pdf,.png,.jpg,.jpeg" onChange={e=>handleFileUpload('pop', e.target.files?.[0])}/>
          </label>

          {/* View uploaded files */}
          {inv.invoice_file && (
            <a href={inv.invoice_file} download={inv.invoice_file_name} title={`Download: ${inv.invoice_file_name}`}
              style={{ display:'inline-flex', alignItems:'center', color:'var(--cyan)', fontSize:'10px' }}>
              <ExternalLink size={9}/>
            </a>
          )}
          {inv.pop_file && (
            <a href={inv.pop_file} download={inv.pop_file_name} title={`Download POP: ${inv.pop_file_name}`}
              style={{ display:'inline-flex', alignItems:'center', color:'var(--green)', fontSize:'10px' }}>
              <ExternalLink size={9}/>
            </a>
          )}

          {/* Archive */}
          {!isArchived && (
            <button className="btn btn-sm" style={{ fontSize:'10px', padding:'2px 7px', color:'var(--amber)', border:'1px solid color-mix(in srgb, var(--amber) 30%, transparent)', background:'color-mix(in srgb, var(--amber) 6%, transparent)', whiteSpace:'nowrap' }} onClick={()=>setShowArchive(true)}>
              Archive
            </button>
          )}
          {isArchived && inv.archive_reason && (
            <span title={`Archived: ${inv.archive_reason}`} style={{ fontSize:'10px', color:'var(--text-muted)', fontStyle:'italic', maxWidth:'100px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {inv.archive_reason}
            </span>
          )}
        </div>

        {showEdit    && <InvoiceEditModal    inv={inv} onClose={()=>setShowEdit(false)}    onSaved={onRefresh}/>}
        {showArchive && <InvoiceArchiveModal inv={inv} onClose={()=>setShowArchive(false)} onSaved={onRefresh}/>}
      </td>
    </tr>
  );
}

function FinancePage() {
  const [summary,    setSummary]   = useState(null);
  const [invoices,   setInvoices]  = useState([]);
  const [companies,  setCompanies] = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [showCreate, setShowCreate]= useState(false);
  const [newInv,     setNewInv]    = useState({ companyId:'', amount:'', discountPct:0, dueDate:'', description:'', notes:'' });
  const [filter,     setFilter]    = useState('all');
  const [collapsed,  setCollapsed] = useState({}); // { 'March 2026': true }

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [s, inv, comp] = await Promise.all([
        axios.get('/api/admin/finance/summary', authH()),
        axios.get('/api/admin/invoices', authH()),
        axios.get('/api/admin/companies', authH()),
      ]);
      setSummary(s.data);
      setInvoices(inv.data);
      setCompanies(comp.data.filter(c => c.slug !== '__tool_pricing__'));
    } catch { toast.error('Failed to load finance data'); }
    finally { setLoading(false); }
  }

  async function createInvoice() {
    if (!newInv.companyId || !newInv.amount) return toast.error('Company and amount required');
    try {
      await axios.post('/api/admin/invoices', {
        companyId:   parseInt(newInv.companyId),
        amountUsd:   parseFloat(newInv.amount),
        discountPct: parseFloat(newInv.discountPct) || 0,
        dueDate:     newInv.dueDate || null,
        description: newInv.description,
        notes:       newInv.notes,
      }, authH());
      toast.success('Invoice created');
      setShowCreate(false);
      setNewInv({ companyId:'', amount:'', discountPct:0, dueDate:'', description:'', notes:'' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  // ── Filter + group by month ──────────────────────────────────────────────────
  const FILTER_MAP = { all:'all', 'yet-to-receive':'pending', received:'paid', overdue:'overdue', archived:'archived' };

  const FILTER_TABS = [
    { id:'all',            label:'All' },
    { id:'yet-to-receive', label:'Yet to Receive' },
    { id:'received',       label:'Received' },
    { id:'overdue',        label:'Overdue' },
    { id:'archived',       label:'Archived' },
  ];

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return inv.status !== 'archived' && inv.status !== 'cancelled';
    const mapped = FILTER_MAP[filter];
    if (filter === 'archived') return inv.status === 'archived' || inv.status === 'cancelled';
    return inv.status === mapped;
  });

  // Group by month/year of created_at
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const groups = {};
  filteredInvoices.forEach(inv => {
    const d    = new Date(inv.created_at);
    const key  = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(inv);
  });

  // Sort groups newest-first
  const groupKeys = Object.keys(groups).sort((a, b) => {
    const parse = s => { const [mon, yr] = s.split(' '); return new Date(`${mon} 1, ${yr}`); };
    return parse(b) - parse(a);
  });

  function toggleGroup(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700 }}>Finance & Payments</h2>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={loadAll}><RefreshCw size={12}/></button>
          <button className="btn btn-primary" onClick={()=>setShowCreate(s=>!s)}><Plus size={13}/> New Invoice</button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'16px' }}>
            {[
              { label:'Yet to Receive', value: fmtUSD(summary.pending.total),  count: summary.pending.count,  color:'var(--amber)' },
              { label:'Received',       value: fmtUSD(summary.paid.total),     count: summary.paid.count,     color:'var(--green)' },
              { label:'Overdue',        value: fmtUSD(summary.overdue.total),  count: summary.overdue.count,  color:'var(--red)'   },
            ].map(({ label, value, count, color }) => (
              <div key={label} className="card" style={{ textAlign:'center', padding:'14px' }}>
                <div style={{ fontSize:'22px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{value}</div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'3px' }}>{label} · {count} invoice{count!==1?'s':''}</div>
              </div>
            ))}
          </div>

          {/* Upcoming renewals */}
          {summary.upcomingRenewals.length > 0 && (
            <div className="card" style={{ marginBottom:'16px', padding:'14px' }}>
              <div style={{ fontWeight:700, fontSize:'12px', color:'var(--amber)', marginBottom:'8px' }}>
                <AlertTriangle size={12} style={{ marginRight:'5px', verticalAlign:'middle' }}/> Renewals due in 30 days
              </div>
              {summary.upcomingRenewals.map((r, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ color:'var(--text-primary)' }}>{r.name}</span>
                  <span style={{ color:'var(--text-muted)' }}>{fmtDate(r.renews_at)}</span>
                  <span style={{ color:'var(--green)', fontFamily:'var(--font-mono)' }}>{fmtUSD(r.final_monthly)}/mo</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="card" style={{ marginBottom:'16px', padding:'16px' }}>
          <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'12px' }}>New Invoice</div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Company *</label>
              <select className="select-field" value={newInv.companyId} onChange={e=>setNewInv(p=>({...p,companyId:e.target.value}))}>
                <option value="">— Select —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Amount (USD) *</label>
              <input className="input-field w-full" type="number" value={newInv.amount} onChange={e=>setNewInv(p=>({...p,amount:e.target.value}))} placeholder="299.00"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Discount %</label>
              <input className="input-field w-full" type="number" min="0" max="100" value={newInv.discountPct} onChange={e=>setNewInv(p=>({...p,discountPct:e.target.value}))}/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Due Date</label>
              <input className="input-field w-full" type="date" value={newInv.dueDate} onChange={e=>setNewInv(p=>({...p,dueDate:e.target.value}))}/>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Description</label>
              <input className="input-field w-full" value={newInv.description} onChange={e=>setNewInv(p=>({...p,description:e.target.value}))} placeholder="e.g. March 2026 subscription"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Internal Notes</label>
              <input className="input-field w-full" value={newInv.notes} onChange={e=>setNewInv(p=>({...p,notes:e.target.value}))}/>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn btn-primary btn-sm" onClick={createInvoice}><FileText size={11}/> Create Invoice</button>
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
        {FILTER_TABS.map(({ id, label }) => {
          const count = id === 'all'
            ? invoices.filter(i => i.status !== 'archived' && i.status !== 'cancelled').length
            : invoices.filter(i => id === 'archived'
                ? i.status === 'archived' || i.status === 'cancelled'
                : i.status === FILTER_MAP[id]).length;
          return (
            <button key={id} className={`btn btn-sm ${filter===id?'btn-primary':'btn-secondary'}`} onClick={()=>setFilter(id)}>
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Invoice list grouped by month */}
      {loading
        ? <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
        : groupKeys.length === 0
          ? <div className="card" style={{ textAlign:'center', padding:'30px', color:'var(--text-muted)', fontSize:'13px' }}>No invoices found</div>
          : groupKeys.map(monthKey => {
              const isCollapsed = collapsed[monthKey];
              const group       = groups[monthKey];
              const groupTotal  = group.reduce((s, i) => s + parseFloat(i.final_amount||0), 0);
              const receivedCount = group.filter(i => i.status === 'paid').length;

              return (
                <div key={monthKey} style={{ marginBottom:'10px' }}>
                  {/* Month header — clickable */}
                  <div onClick={()=>toggleGroup(monthKey)}
                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'var(--bg-elevated)',
                      borderRadius: isCollapsed ? 'var(--radius-sm)' : 'var(--radius-sm) var(--radius-sm) 0 0',
                      border:'1px solid var(--border)', cursor:'pointer', userSelect:'none' }}>
                    {isCollapsed
                      ? <ChevronRight size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
                      : <ChevronDown  size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/>}
                    <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)', flex:1 }}>{monthKey}</span>
                    <span className="badge badge-muted" style={{ fontSize:'10px' }}>{group.length} invoice{group.length!==1?'s':''}</span>
                    <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>{receivedCount} received</span>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:'12px', color:'var(--green)', fontWeight:700 }}>{fmtUSD(groupTotal)}</span>
                  </div>

                  {/* Table — shown when expanded */}
                  {!isCollapsed && (
                    <div style={{ border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius-sm) var(--radius-sm)', overflow:'hidden' }}>
                      <div style={{ overflowX:'auto' }}>
                        <table className="data-table" style={{ minWidth:'980px' }}>
                          <thead>
                            <tr>
                              <th>Invoice #</th>
                              <th>Company</th>
                              <th>Description</th>
                              <th>Amount</th>
                              <th>Discount</th>
                              <th>Final</th>
                              <th>Status</th>
                              <th>Due</th>
                              <th>Received On</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.map(inv => (
                              <InvoiceRow key={inv.id} inv={inv} onRefresh={loadAll}/>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
      }
    </div>
  );
}


function ChangeLogPage() {
  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]   = useState(0);
  const [filter, setFilter] = useState('');
  const PER_PAGE = 50;

  useEffect(() => { load(0); }, []);

  async function load(p) {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/admin/change-log?limit=${PER_PAGE}&offset=${p*PER_PAGE}`, authH());
      setRows(data.rows);
      setTotal(data.total);
      setPage(p);
    } catch { toast.error('Failed to load change log'); }
    finally { setLoading(false); }
  }

  const ACTION_COLOR = {
    CREATE_COMPANY: 'var(--green)', EDIT_COMPANY: 'var(--cyan)',
    DELETE_COMPANY: 'var(--red)', SUSPEND_COMPANY: 'var(--amber)',
    REACTIVATE_COMPANY: 'var(--green)', IMPERSONATE: 'var(--purple)',
    CREATE_INVOICE: 'var(--green)', RECORD_PAYMENT: 'var(--green)',
    UPDATE_PRICING: 'var(--amber)', ADD_ADMIN: 'var(--purple)',
    DELETE_ADMIN: 'var(--red)', RESET_PASSWORD: 'var(--amber)',
    ADD_USER: 'var(--cyan)',
  };

  const filtered = rows.filter(r => !filter || r.action.includes(filter.toUpperCase()) || r.entity_name?.toLowerCase().includes(filter.toLowerCase()) || r.admin_email?.includes(filter));

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700 }}>Change Log</h2>
        <button className="btn btn-secondary btn-sm" onClick={()=>load(page)}><RefreshCw size={12}/></button>
      </div>
      <input className="input-field" style={{ marginBottom:'12px', width:'320px' }} placeholder="Filter by action, company, admin..." value={filter} onChange={e=>setFilter(e.target.value)}/>
      {loading ? <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div> : (
        <>
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table className="data-table">
              <thead>
                <tr><th>Time</th><th>Admin</th><th>Action</th><th>Entity</th><th>IP</th><th>Details</th></tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize:'11px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{fmtDateTime(r.created_at)}</td>
                    <td style={{ fontSize:'11px' }}>{r.admin_email}</td>
                    <td><span style={{ fontSize:'10px', fontWeight:700, color: ACTION_COLOR[r.action] || 'var(--text-muted)', background:`color-mix(in srgb, ${ACTION_COLOR[r.action] || 'var(--text-muted)'} 8%, transparent)`, padding:'2px 6px', borderRadius:'3px', whiteSpace:'nowrap' }}>{r.action}</span></td>
                    <td style={{ fontSize:'11px' }}>{r.entity_name || r.entity_type}</td>
                    <td style={{ fontSize:'10px', fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>{r.ip_address || '—'}</td>
                    <td>
                      {r.changes && Object.keys(r.changes).length > 0 && (
                        <details style={{ fontSize:'10px' }}>
                          <summary style={{ cursor:'pointer', color:'var(--text-muted)' }}>View</summary>
                          <pre style={{ fontFamily:'var(--font-mono)', fontSize:'10px', marginTop:'4px', color:'var(--text-secondary)', whiteSpace:'pre-wrap', maxWidth:'300px' }}>
                            {JSON.stringify(r.changes, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PER_PAGE && (
            <div style={{ display:'flex', gap:'8px', justifyContent:'center', marginTop:'12px', alignItems:'center', fontSize:'12px', color:'var(--text-muted)' }}>
              <button className="btn btn-secondary btn-sm" onClick={()=>load(page-1)} disabled={page===0}>← Prev</button>
              <span>Page {page+1} of {Math.ceil(total/PER_PAGE)} ({total} entries)</span>
              <button className="btn btn-secondary btn-sm" onClick={()=>load(page+1)} disabled={(page+1)*PER_PAGE>=total}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Admin Users Page ─────────────────────────────────────────────────────────
function AdminUsersPage() {
  const [admins, setAdmins]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm]         = useState({ email:'', password:'', name:'', role:'admin' });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/admin-users', authH());
      setAdmins(data);
    } catch { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  }

  async function addAdmin() {
    if (!form.email || !form.password) return toast.error('Email and password required');
    try {
      await axios.post('/api/admin/admin-users', form, authH());
      toast.success('Admin added');
      setShowAdd(false); setForm({ email:'', password:'', name:'', role:'admin' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function saveEdit(id) {
    const payload = {};
    if (editForm.name  !== undefined) payload.name  = editForm.name;
    if (editForm.role  !== undefined) payload.role  = editForm.role;
    if (editForm.password?.trim())    payload.password = editForm.password.trim();
    try {
      await axios.patch(`/api/admin/admin-users/${id}`, payload, authH());
      toast.success('Admin updated');
      setEditId(null); setEditForm({});
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function toggleActive(id, is_active) {
    try {
      await axios.patch(`/api/admin/admin-users/${id}`, { is_active: !is_active }, authH());
      toast.success(is_active ? 'Admin deactivated' : 'Admin activated');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function deleteAdmin(id, email) {
    if (!window.confirm(`Remove admin access for ${email}?`)) return;
    try {
      await axios.delete(`/api/admin/admin-users/${id}`, authH());
      toast.success('Admin removed');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  function startEdit(a) {
    setEditId(a.id);
    setEditForm({ name: a.name || '', role: a.role || 'admin', password: '' });
    setShowAdd(false);
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'4px' }}>Admin Users</h2>
          <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Manage who has access to this Admin Panel</div>
        </div>
        <button className="btn btn-primary" onClick={()=>{ setShowAdd(s=>!s); setEditId(null); }}><Plus size={13}/> Add Admin</button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card" style={{ marginBottom:'16px', padding:'16px' }}>
          <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'12px' }}>Add New Admin</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Name</label>
              <input className="input-field w-full" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Jane Smith"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Email *</label>
              <input className="input-field w-full" type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="jane@ignitorads.com"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Password * (8+ chars)</label>
              <input className="input-field w-full" type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="••••••••"/>
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Role *</label>
              <select className="input-field w-full" value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
                {ADMIN_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'4px' }}>{getRoleInfo(form.role).desc}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn btn-primary btn-sm" onClick={addAdmin}><UserCheck size={11}/> Add Admin</button>
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Last Login</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {admins.map(a => (
                <React.Fragment key={a.id}>
                  <tr>
                    <td>{a.name || '—'}</td>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:'12px' }}>{a.email}</td>
                    <td>
                      {a.is_super
                        ? <span className="badge badge-cyan" style={{ fontSize:'10px' }}>Super Admin</span>
                        : <span className={`badge ${getRoleInfo(a.role||'admin').badge}`} style={{ fontSize:'10px' }}>{getRoleInfo(a.role||'admin').label}</span>
                      }
                    </td>
                    <td style={{ fontSize:'11px', color:'var(--text-muted)' }}>{fmtDateTime(a.last_login)}</td>
                    <td><span className={`badge ${a.is_active?'badge-green':'badge-muted'}`} style={{ fontSize:'10px' }}>{a.is_active?'Active':'Inactive'}</span></td>
                    <td>
                      {!a.is_super && (
                        <div style={{ display:'flex', gap:'4px' }}>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px', padding:'2px 8px' }}
                            onClick={() => editId === a.id ? (setEditId(null), setEditForm({})) : startEdit(a)}>
                            <Edit2 size={9}/> {editId === a.id ? 'Cancel' : 'Edit'}
                          </button>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px', padding:'2px 8px' }} onClick={()=>toggleActive(a.id, a.is_active)}>
                            {a.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize:'10px', padding:'2px 8px' }} onClick={()=>deleteAdmin(a.id, a.email)}>
                            <Trash2 size={9}/>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Inline edit row */}
                  {editId === a.id && (
                    <tr style={{ background:'color-mix(in srgb, var(--cyan) 3%, transparent)' }}>
                      <td colSpan={6} style={{ padding:'12px 16px' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:'10px', alignItems:'flex-end' }}>
                          <div>
                            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Name</label>
                            <input className="input-field w-full" value={editForm.name}
                              onChange={e=>setEditForm(p=>({...p,name:e.target.value}))} placeholder="Full name"/>
                          </div>
                          <div>
                            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Role</label>
                            <select className="input-field w-full" value={editForm.role}
                              onChange={e=>setEditForm(p=>({...p,role:e.target.value}))}>
                              {ADMIN_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>
                              New Password <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(leave blank to keep)</span>
                            </label>
                            <input className="input-field w-full" type="password" value={editForm.password}
                              onChange={e=>setEditForm(p=>({...p,password:e.target.value}))} placeholder="••••••••"/>
                          </div>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <button className="btn btn-primary btn-sm" onClick={()=>saveEdit(a.id)}>
                              <Save size={10}/> Save
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={()=>{ setEditId(null); setEditForm({}); }}>
                              ✕
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'6px' }}>
                          Role: {getRoleInfo(editForm.role).desc}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Support Tickets Page ─────────────────────────────────────────────────────
const TICKET_STATUS_COLORS = {
  open:        { color:'var(--red)',   bg:'color-mix(in srgb, var(--red) 10%, transparent)',   border:'color-mix(in srgb, var(--red) 30%, transparent)',   label:'Open'        },
  in_progress: { color:'var(--amber)', bg:'color-mix(in srgb, var(--amber) 10%, transparent)',   border:'color-mix(in srgb, var(--amber) 30%, transparent)',   label:'In Progress' },
  resolved:    { color:'var(--green)', bg:'color-mix(in srgb, var(--green) 8%, transparent)',  border:'color-mix(in srgb, var(--green) 20%, transparent)',   label:'Resolved'    },
  closed:      { color:'var(--text-muted)', bg:'rgba(0,0,0,0)',    border:'var(--border)',          label:'Closed'      },
};
const TICKET_PRIORITY_COLORS = {
  low:      { color:'var(--text-muted)', label:'Low'    },
  normal:   { color:'var(--cyan)',       label:'Normal' },
  high:     { color:'var(--amber)',      label:'High'   },
  urgent:   { color:'var(--red)',        label:'Urgent' },
};

function AdminTicketThread({ ticketId, onBack, onRefresh }) {
  const [full, setFull]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [replyMsg, setReplyMsg]   = useState('');
  const [sending, setSending]     = useState(false);
  const [updatingId, setUpdating] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const bottomRef                  = React.useRef(null);
  const fileInputRef               = React.useRef(null);

  useEffect(() => { load(); }, [ticketId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [full?.replies]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/admin/support-tickets/${ticketId}`, authH());
      setFull(data);
    } catch { toast.error('Failed to load ticket'); }
    finally { setLoading(false); }
  }

  async function handleFilePick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      if (!ATT_ALLOWED_MIME.has((f.type || '').toLowerCase())) { toast.error(`"${f.name}" type not allowed`); continue; }
      if (f.size > ATT_MAX_FILE_BYTES) { toast.error(`"${f.name}" exceeds 5 MB`); continue; }
      if (pendingFiles.length >= 10) { toast.error('Max 10 files per message'); break; }
      try {
        const att = await readFileAsAttachment(f);
        setPendingFiles(prev => prev.length < 10 ? [...prev, att] : prev);
      } catch { toast.error(`Could not read "${f.name}"`); }
    }
  }
  function removePending(idx) { setPendingFiles(prev => prev.filter((_, i) => i !== idx)); }

  async function sendReply() {
    if (!replyMsg.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      await axios.post(`/api/admin/support-tickets/${ticketId}/reply`, { message: replyMsg.trim(), attachments: pendingFiles }, authH());
      setReplyMsg('');
      setPendingFiles([]);
      await load();
      onRefresh();
      toast.success('Reply sent');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to send reply'); }
    finally { setSending(false); }
  }

  async function updateStatus(status) {
    setUpdating(status);
    try {
      await axios.patch(`/api/admin/support-tickets/${ticketId}`, { status, resolved_at: status === 'resolved' ? new Date().toISOString() : null }, authH());
      setFull(prev => ({ ...prev, status }));
      onRefresh();
      toast.success('Status updated');
    } catch { toast.error('Update failed'); }
    finally { setUpdating(null); }
  }

  if (!full && loading) return <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>;

  const sc = TICKET_STATUS_COLORS[full?.status] || TICKET_STATUS_COLORS.open;
  const pc = TICKET_PRIORITY_COLORS[full?.priority] || TICKET_PRIORITY_COLORS.normal;
  const itype = ADMIN_ISSUE_TYPES[full?.issue_type];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}><ChevronRight size={12} style={{ transform:'rotate(180deg)' }}/> Back</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'3px' }}>
            <span style={{ fontWeight:700, fontSize:'15px', color:'var(--text-primary)' }}>#{ticketId} — {full?.subject}</span>
            <span style={{ fontSize:'10px', fontWeight:700, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:'4px', padding:'2px 7px' }}>{sc.label}</span>
            <span style={{ fontSize:'9px', fontWeight:700, color:pc.color, background:`color-mix(in srgb, ${pc.color} 13%, transparent)`, border:`1px solid color-mix(in srgb, ${pc.color} 25%, transparent)`, borderRadius:'3px', padding:'1px 5px', textTransform:'uppercase' }}>{pc.label}</span>
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', display:'flex', gap:'12px', flexWrap:'wrap' }}>
            <span style={{ color:'var(--cyan)', fontWeight:600 }}>{full?.company_name}</span>
            <span>{full?.user_email}</span>
            {itype && <span>{itype.icon} {itype.label}</span>}
            <span>{fmtDateTime(full?.created_at)}</span>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={11} style={{ animation:loading?'spin 1s linear infinite':'none' }}/>
        </button>
      </div>

      <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'11px', color:'var(--text-muted)', fontWeight:600 }}>Status:</span>
        {Object.entries(TICKET_STATUS_COLORS).map(([k, v]) => (
          <button key={k} className={`btn btn-sm ${full?.status === k ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize:'10px', padding:'3px 10px', color: full?.status === k ? undefined : v.color }}
            disabled={!!updatingId} onClick={() => updateStatus(k)}>
            {v.label}
          </button>
        ))}
      </div>

      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden', marginBottom:'16px' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
        ) : (
          <div style={{ maxHeight:'460px', overflowY:'auto', padding:'16px', display:'flex', flexDirection:'column', gap:'12px' }}>
            {(full?.replies || []).map((reply, i) => {
              const isAdmin = reply.author_type === 'admin';
              return (
                <div key={reply.id || i} style={{ display:'flex', gap:'10px', flexDirection:isAdmin?'row-reverse':'row' }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', flexShrink:0, background:isAdmin?'color-mix(in srgb, var(--cyan) 15%, transparent)':'color-mix(in srgb, var(--purple) 15%, transparent)', border:`1px solid color-mix(in srgb, ${isAdmin?'var(--cyan)':'var(--purple)'} 25%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:800, color:isAdmin?'var(--cyan)':'var(--purple)' }}>
                    {isAdmin ? 'IG' : (reply.author_name?.[0] || 'C').toUpperCase()}
                  </div>
                  <div style={{ maxWidth:'75%' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'4px', flexDirection:isAdmin?'row-reverse':'row' }}>
                      <span style={{ fontSize:'11px', fontWeight:700, color:isAdmin?'var(--cyan)':'var(--purple)' }}>{isAdmin?'IgnitorAds Support':(reply.author_name||full?.company_name||'Company')}</span>
                      {isAdmin && <span className="badge badge-cyan" style={{ fontSize:'9px' }}>Support</span>}
                      <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{fmtDateTime(reply.created_at)}</span>
                    </div>
                    <div style={{ padding:'10px 14px', borderRadius:isAdmin?'14px 4px 14px 14px':'4px 14px 14px 14px', background:isAdmin?'color-mix(in srgb, var(--cyan) 6%, transparent)':'color-mix(in srgb, var(--purple) 6%, transparent)', border:`1px solid ${isAdmin?'color-mix(in srgb, var(--cyan) 15%, transparent)':'color-mix(in srgb, var(--purple) 15%, transparent)'}`, fontSize:'13px', color:'var(--text-primary)', lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                      {reply.message}
                      {(reply.attachments || []).length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop: reply.message ? '10px' : 0 }}>
                          {reply.attachments.map(att => (
                            <a key={att.id}
                              href={`/api/admin/support-tickets/${ticketId}/attachments/${att.id}`}
                              target="_blank" rel="noopener noreferrer"
                              title={`${att.file_name} · ${fmtAttBytes(att.size_bytes)}`}
                              style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 9px', borderRadius:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', textDecoration:'none', color:'var(--text-primary)', fontSize:'11px', maxWidth:'200px' }}>
                              <FileText size={12} style={{ color:'var(--cyan)', flexShrink:0 }} />
                              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{att.file_name}</span>
                              <Download size={11} style={{ color:'var(--text-muted)', flexShrink:0 }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
        <div style={{ padding:'14px 16px', borderTop:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
          <div style={{ fontSize:'11px', color:'var(--cyan)', fontWeight:600, marginBottom:'6px' }}>Reply as IgnitorAds Support</div>
          <textarea className="input-field" style={{ width:'100%', minHeight:'80px', resize:'vertical', fontSize:'13px', lineHeight:1.6, marginBottom:'8px' }}
            placeholder="Type your reply to the company..."
            value={replyMsg} onChange={e => setReplyMsg(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply(); }}/>
          {pendingFiles.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'8px' }}>
              {pendingFiles.map((f, i) => (
                <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 8px', borderRadius:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'11px', maxWidth:'200px' }}>
                  <FileText size={11} style={{ color:'var(--cyan)', flexShrink:0 }} />
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.file_name}</span>
                  <span style={{ color:'var(--text-muted)' }}>{fmtAttBytes(f.size_bytes)}</span>
                  <button onClick={() => removePending(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', padding:0, display:'flex' }}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple accept={ATT_ACCEPT_TYPES} style={{ display:'none' }} onChange={handleFilePick} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={sending || pendingFiles.length >= 10}>
                <Paperclip size={11} /> Attach
              </button>
              <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>Ctrl+Enter · max 5 MB/file · Company will be notified</span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={sendReply} disabled={sending || (!replyMsg.trim() && pendingFiles.length === 0)}>
              {sending ? <><RefreshCw size={10} style={{ animation:'spin 1s linear infinite' }}/> Sending...</> : <><Save size={10}/> Send Reply</>}
            </button>
          </div>
        </div>
      </div>
      <AdminNotesEditor ticket={{ id: ticketId, admin_notes: full?.admin_notes || '' }}
        onSave={async patch => {
          try { await axios.patch(`/api/admin/support-tickets/${ticketId}`, patch, authH()); setFull(prev => ({ ...prev, ...patch })); toast.success('Notes saved'); }
          catch { toast.error('Save failed'); }
        }} saving={false} />
    </div>
  );
}

const ADMIN_ISSUE_TYPES = {
  login:       { label: 'Login Issues',        icon: '🔐' },
  bug:         { label: 'Noticed a Bug',        icon: '🐛' },
  feature:     { label: 'New Feature Request',  icon: '✨' },
  enhancement: { label: 'Tool Enhancement',     icon: '🔧' },
  outage:      { label: 'Outage',               icon: '🚨' },
  billing:     { label: 'Billing',              icon: '💳' },
  other:       { label: 'Other',                icon: '💬' },
};

function SupportTicketsPage() {
  const [tickets, setTickets]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterStatus, setFS]       = useState('all');
  const [filterPriority, setFP]     = useState('all');
  const [searchQ, setSearchQ]       = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { const { data } = await axios.get('/api/admin/support-tickets', authH()); setTickets(data); }
    catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }

  const filtered = tickets.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    if (searchQ) { const q = searchQ.toLowerCase(); return t.subject?.toLowerCase().includes(q) || t.company_name?.toLowerCase().includes(q) || t.user_email?.toLowerCase().includes(q); }
    return true;
  });

  const counts = {
    open:        tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved:    tickets.filter(t => t.status === 'resolved').length,
  };

  if (selectedId) return <AdminTicketThread ticketId={selectedId} onBack={() => { setSelectedId(null); load(); }} onRefresh={load} />;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'4px' }}>Support Tickets</h2>
          <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>All tickets submitted by companies via the Support page</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/> Refresh</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'16px' }}>
        {[ { key:'open', label:'Open', color:'var(--red)' }, { key:'in_progress', label:'In Progress', color:'var(--amber)' }, { key:'resolved', label:'Resolved', color:'var(--green)' } ].map(({ key, label, color }) => (
          <div key={key} className="stat-card" onClick={() => setFS(filterStatus === key ? 'all' : key)} style={{ cursor:'pointer', border:`1px solid ${filterStatus === key ? color : 'var(--border)'}`, background: filterStatus === key ? `color-mix(in srgb, ${color} 6%, transparent)` : 'var(--bg-card)' }}>
            <div className="stat-label">{label}</div>
            <div className="stat-value" style={{ color }}>{counts[key] ?? 0}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
        <input className="input-field" style={{ width:'260px' }} placeholder="Search by subject, company, email..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
        <select className="input-field" style={{ width:'140px' }} value={filterStatus} onChange={e=>setFS(e.target.value)}>
          <option value="all">All Status</option>
          {Object.entries(TICKET_STATUS_COLORS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="input-field" style={{ width:'130px' }} value={filterPriority} onChange={e=>setFP(e.target.value)}>
          <option value="all">All Priority</option>
          {Object.entries(TICKET_PRIORITY_COLORS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span style={{ fontSize:'11px', color:'var(--text-muted)', marginLeft:'auto' }}>{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      {loading ? (
        <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'40px', color:'var(--text-muted)' }}>
          <FileText size={28} style={{ marginBottom:'12px', opacity:0.4 }}/><div style={{ fontSize:'13px' }}>No tickets found</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {filtered.map(ticket => {
            const sc = TICKET_STATUS_COLORS[ticket.status] || TICKET_STATUS_COLORS.open;
            const pc = TICKET_PRIORITY_COLORS[ticket.priority] || TICKET_PRIORITY_COLORS.normal;
            const itype = ADMIN_ISSUE_TYPES[ticket.issue_type];
            return (
              <div key={ticket.id} onClick={() => setSelectedId(ticket.id)}
                style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden', cursor:'pointer', transition:'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `color-mix(in srgb, ${sc.color} 38%, transparent)`}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:sc.color, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'3px' }}>
                      <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>#{ticket.id} — {ticket.subject}</span>
                      <span style={{ fontSize:'9px', fontWeight:700, color:pc.color, background:`color-mix(in srgb, ${pc.color} 13%, transparent)`, border:`1px solid color-mix(in srgb, ${pc.color} 25%, transparent)`, borderRadius:'3px', padding:'1px 5px', textTransform:'uppercase' }}>{pc.label}</span>
                      {itype && <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{itype.icon} {itype.label}</span>}
                    </div>
                    <div style={{ display:'flex', gap:'10px', fontSize:'11px', color:'var(--text-muted)', flexWrap:'wrap' }}>
                      <span style={{ color:'var(--cyan)', fontWeight:600 }}>{ticket.company_name||'Unknown'}</span>
                      <span>{ticket.user_email}</span>
                      <span>{fmtDateTime(ticket.created_at)}</span>
                      {ticket.reply_count > 0 && <span>💬 {ticket.reply_count} message{ticket.reply_count!=='1'?'s':''}</span>}
                    </div>
                  </div>
                  <span style={{ fontSize:'10px', fontWeight:700, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:'4px', padding:'3px 8px', whiteSpace:'nowrap' }}>{sc.label}</span>
                  <ChevronRight size={14} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminNotesEditor({ ticket, onSave, saving }) {
  const [notes, setNotes] = useState(ticket.admin_notes || '');
  const [dirty, setDirty] = useState(false);

  function handleChange(v) { setNotes(v); setDirty(v !== (ticket.admin_notes || '')); }

  return (
    <div style={{ marginTop:'14px', borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
      <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'5px', fontWeight:600 }}>Internal Notes (not visible to customer)</label>
      <textarea className="input-field w-full" style={{ minHeight:'72px', fontSize:'12px', resize:'vertical' }}
        value={notes} onChange={e => handleChange(e.target.value)}
        placeholder="Add internal notes about this ticket..."/>
      {dirty && (
        <button className="btn btn-primary btn-sm" style={{ marginTop:'6px' }} disabled={saving}
          onClick={() => { onSave({ admin_notes: notes }); setDirty(false); }}>
          {saving ? <><RefreshCw size={10} style={{ animation:'spin 1s linear infinite' }}/> Saving...</> : <><Save size={10}/> Save Notes</>}
        </button>
      )}
    </div>
  );
}

// ─── Theme Page ───────────────────────────────────────────────────────────────

// ─── AI Support Page ──────────────────────────────────────────────────────────
const DEFAULT_RULEBOOK = [
  { issue_type:'login',       rule:'Ask for their email address and browser/device. Check if they have tried a password reset. Reassure them their data is safe.', example:'Thanks for reaching out. Could you confirm the email address you use to sign in? Also, have you tried the password reset option?' },
  { issue_type:'bug',         rule:'Ask for steps to reproduce, the tool name, any error messages, and their browser. Acknowledge the issue and let them know you are investigating.', example:'Thanks for flagging this. To help us look into it, could you share the exact steps to reproduce the issue and any error messages you saw?' },
  { issue_type:'feature',     rule:'Thank them for the suggestion, confirm it has been logged for the product team, and set realistic expectations (no firm timeline).', example:'Great idea — we have logged this for our product team to review. We will be in touch once we have an update.' },
  { issue_type:'enhancement', rule:'Acknowledge the gap, confirm it is on the roadmap or will be evaluated. Ask for more detail if needed.', example:'Understood — that is a useful enhancement. We have noted it for our next product review cycle.' },
  { issue_type:'outage',      rule:'This is urgent — immediately escalate. Acknowledge the impact, confirm you are investigating, and set a follow-up time. Do NOT auto-reply if confidence < 80.', example:'We are aware of the issue and our team is actively investigating. We will update you within the hour.' },
  { issue_type:'billing',     rule:'Handle carefully. If it involves charges or account access, escalate to human. Only auto-reply for general billing questions.', example:'Happy to help with that. Could you share more details about the billing query so we can look into it?' },
  { issue_type:'other',       rule:'Acknowledge the message, ask one clarifying question to understand the issue better.', example:'Thanks for getting in touch. Could you share a bit more detail so we can point you in the right direction?' },
];

const ISSUE_LABELS = {
  login:'Login Issues', bug:'Noticed a Bug', feature:'New Feature Request',
  enhancement:'Tool Enhancement', outage:'Outage', billing:'Billing', other:'Other',
};

const ACTION_STYLES = {
  replied:      { color:'var(--green)',  label:'Auto-replied' },
  queued_draft: { color:'var(--amber)',  label:'Pending review' },
  approved:     { color:'var(--cyan)',   label:'Approved' },
  rejected:     { color:'var(--text-muted)', label:'Rejected' },
  queued:       { color:'var(--purple)', label:'Queued' },
};

function AISupportPage() {
  const [tab, setTab]           = useState('settings');
  const [config, setConfig]     = useState(null);
  const [queue, setQueue]       = useState([]);
  const [log, setLog]           = useState([]);
  const [stats, setStats]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editDraft, setEditDraft] = useState(null); // {id, message}
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [cfgR, queueR, logR, statsR] = await Promise.all([
        axios.get('/api/admin/ai-support/config', authH()),
        axios.get('/api/admin/ai-support/queue', authH()),
        axios.get('/api/admin/ai-support/log', authH()),
        axios.get('/api/admin/ai-support/stats', authH()),
      ]);
      // Merge default rulebook with saved — ensure all issue types present
      const savedRulebook = cfgR.data.rulebook || [];
      const mergedRulebook = DEFAULT_RULEBOOK.map(def => {
        const saved = savedRulebook.find(r => r.issue_type === def.issue_type);
        return saved || def;
      });
      setConfig({ ...cfgR.data, rulebook: mergedRulebook });
      setQueue(queueR.data);
      setLog(logR.data);
      setStats(statsR.data);
    } catch (err) { toast.error('Failed to load AI Support config'); }
    finally { setLoading(false); }
  }

  async function saveConfig() {
    setSaving(true);
    try {
      await axios.patch('/api/admin/ai-support/config', config, authH());
      toast.success('Configuration saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function approveDraft(id) {
    const msg = editDraft?.id === id ? editDraft.message : undefined;
    try {
      await axios.post(`/api/admin/ai-support/queue/${id}/approve`, { message: msg }, authH());
      toast.success('Reply sent');
      setEditDraft(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function rejectDraft(id) {
    if (!window.confirm('Reject this draft? No reply will be sent.')) return;
    try {
      await axios.post(`/api/admin/ai-support/queue/${id}/reject`, {}, authH());
      toast.success('Draft rejected');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function testTicket(ticketId) {
    if (!ticketId) return toast.error('Enter a ticket ID');
    setTestResult(null);
    try {
      const { data } = await axios.post(`/api/admin/ai-support/test/${ticketId}`, {}, authH());
      setTestResult(data);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Test failed'); }
  }

  function updateRule(issueType, field, val) {
    setConfig(prev => ({
      ...prev,
      rulebook: prev.rulebook.map(r => r.issue_type === issueType ? { ...r, [field]: val } : r),
    }));
  }

  if (loading) return <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>;
  if (!config)  return <div style={{ textAlign:'center', padding:'40px', color:'var(--text-muted)', fontSize:'13px' }}>Failed to load AI Support configuration. Check Railway logs.</div>;

  const pendingCount = queue.length;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'4px', display:'flex', alignItems:'center', gap:'8px' }}>
            <Bot size={16} style={{ color:'var(--cyan)' }}/> AI Support
          </h2>
          <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Claude handles tickets automatically. Feels human to companies.</div>
        </div>
        {/* Master toggle */}
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <span style={{ fontSize:'12px', color:'var(--text-muted)' }}>AI Auto-reply</span>
          <div onClick={() => setConfig(p => ({...p, is_enabled: !p.is_enabled}))}
            style={{ width:44, height:24, borderRadius:12, background: config.is_enabled ? 'var(--accent)' : 'var(--track-off)', border:'1px solid var(--border)', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
            <div style={{ position:'absolute', top:3, left: config.is_enabled ? 22 : 3, width:16, height:16, borderRadius:'50%', background: config.is_enabled ? 'var(--on-accent)' : 'var(--knob)', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
          </div>
          <span style={{ fontSize:'11px', fontWeight:700, color: config.is_enabled ? 'var(--green)' : 'var(--text-muted)' }}>
            {config.is_enabled ? 'ON' : 'OFF'}
          </span>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'8px', marginBottom:'20px' }}>
        {[
          { label:'Auto-replied',  value: stats.auto_replied  || 0, color:'var(--green)'  },
          { label:'Pending review',value: stats.pending_review || 0, color:'var(--amber)' },
          { label:'Approved',      value: stats.approved      || 0, color:'var(--cyan)'   },
          { label:'Rejected',      value: stats.rejected      || 0, color:'var(--text-muted)' },
          { label:'Avg confidence',value: stats.avg_confidence ? stats.avg_confidence + '%' : '—', color:'var(--purple)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize:'18px' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'16px', borderBottom:'1px solid var(--border)', paddingBottom:'4px' }}>
        {[
          { id:'settings', label:'Settings' },
          { id:'rulebook', label:'Rulebook' },
          { id:'queue',    label:`Review Queue${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { id:'log',      label:'Response Log' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:'11px' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Settings tab ─────────────────────────────────────────────────────── */}
      {tab === 'settings' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            {/* Confidence threshold */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'12px' }}>Confidence Threshold</div>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px' }}>
                <input type="range" min={50} max={95} value={config.confidence_threshold || 75}
                  onChange={e => setConfig(p => ({...p, confidence_threshold: parseInt(e.target.value)}))}
                  style={{ flex:1 }}/>
                <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--cyan)', minWidth:'36px' }}>
                  {config.confidence_threshold || 75}%
                </span>
              </div>
              <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                Replies above this score are sent automatically. Below → queued for your review.
              </div>
            </div>

            {/* Delay range */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'12px' }}>Reply Delay (human-feel)</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Min (seconds)</label>
                  <input className="input-field w-full" type="number" min={30} max={600}
                    value={config.delay_min_seconds || 120}
                    onChange={e => setConfig(p => ({...p, delay_min_seconds: parseInt(e.target.value)}))}/>
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Max (seconds)</label>
                  <input className="input-field w-full" type="number" min={60} max={1800}
                    value={config.delay_max_seconds || 600}
                    onChange={e => setConfig(p => ({...p, delay_max_seconds: parseInt(e.target.value)}))}/>
                </div>
              </div>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'8px' }}>
                Currently: {Math.round((config.delay_min_seconds||120)/60)}–{Math.round((config.delay_max_seconds||600)/60)} minute random delay before sending.
              </div>
            </div>

            {/* Manual test */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'8px' }}>Manual Test</div>
              <div style={{ display:'flex', gap:'8px' }}>
                <input className="input-field" type="number" placeholder="Ticket ID" style={{ width:'120px' }}
                  id="test-ticket-id"/>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  const id = document.getElementById('test-ticket-id').value;
                  testTicket(id);
                }}><Bot size={11}/> Test Now</button>
              </div>
              {testResult && (
                <div style={{ marginTop:'10px', padding:'10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', fontSize:'12px' }}>
                  <div style={{ fontWeight:700, color: testResult.action === 'replied' ? 'var(--green)' : testResult.action === 'queued_draft' ? 'var(--amber)' : 'var(--text-muted)', marginBottom:'4px' }}>
                    {testResult.action || testResult.skipped ? (testResult.action || 'Skipped') : 'Error'}
                  </div>
                  {testResult.confidence && <div style={{ color:'var(--text-muted)' }}>Confidence: {testResult.confidence}%</div>}
                  {testResult.reason && <div style={{ color:'var(--text-muted)' }}>{testResult.reason}</div>}
                  {testResult.error && <div style={{ color:'var(--red)' }}>{testResult.error}</div>}
                </div>
              )}
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            {/* Persona */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'8px' }}>AI Persona</div>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px' }}>How Claude understands its role. Keep it brief and on-brand.</div>
              <textarea className="input-field w-full" rows={4} style={{ fontSize:'12px', lineHeight:1.6, resize:'vertical' }}
                value={config.persona || ''}
                onChange={e => setConfig(p => ({...p, persona: e.target.value}))}/>
            </div>
            {/* Signature */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', marginBottom:'8px' }}>Reply Signature</div>
              <textarea className="input-field w-full" rows={3} style={{ fontSize:'12px', lineHeight:1.6, resize:'vertical' }}
                value={config.signature || ''}
                onChange={e => setConfig(p => ({...p, signature: e.target.value}))}/>
            </div>
          </div>

          {/* Save button */}
          <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
            <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
              {saving ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Saving...</> : <><Save size={12}/> Save Configuration</>}
            </button>
          </div>
        </div>
      )}

      {/* ── Rulebook tab ──────────────────────────────────────────────────────── */}
      {tab === 'rulebook' && (
        <div>
          <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'16px' }}>
            Define how the AI should handle each issue type. The AI reads these rules before drafting every reply.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            {(config.rulebook || []).map(rule => (
              <div key={rule.issue_type} className="card" style={{ padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
                  <span style={{ fontSize:'16px' }}>
                    {({login:'🔐',bug:'🐛',feature:'✨',enhancement:'🔧',outage:'🚨',billing:'💳',other:'💬'})[rule.issue_type]}
                  </span>
                  <span style={{ fontWeight:700, fontSize:'13px' }}>{ISSUE_LABELS[rule.issue_type] || rule.issue_type}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Rule / Instruction</label>
                    <textarea className="input-field w-full" rows={4} style={{ fontSize:'12px', lineHeight:1.6, resize:'vertical' }}
                      value={rule.rule || ''}
                      onChange={e => updateRule(rule.issue_type, 'rule', e.target.value)}
                      placeholder="How should AI handle this issue type?"/>
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Example Response Style</label>
                    <textarea className="input-field w-full" rows={4} style={{ fontSize:'12px', lineHeight:1.6, resize:'vertical' }}
                      value={rule.example || ''}
                      onChange={e => updateRule(rule.issue_type, 'example', e.target.value)}
                      placeholder="Optional example to guide the tone..."/>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'16px' }}>
            <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
              {saving ? 'Saving...' : <><Save size={12}/> Save Rulebook</>}
            </button>
          </div>
        </div>
      )}

      {/* ── Review Queue tab ──────────────────────────────────────────────────── */}
      {tab === 'queue' && (
        <div>
          {queue.length === 0 ? (
            <div className="card" style={{ padding:'40px', textAlign:'center', color:'var(--text-muted)' }}>
              <Bot size={28} style={{ marginBottom:'12px', opacity:0.4 }}/>
              <div style={{ fontSize:'13px' }}>No drafts pending review. AI is handling tickets confidently.</div>
            </div>
          ) : queue.map(item => (
            <div key={item.id} className="card" style={{ padding:'16px', marginBottom:'10px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:'12px', marginBottom:'12px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'4px' }}>
                    <span style={{ fontWeight:700, fontSize:'13px' }}>#{item.ticket_id} — {item.subject}</span>
                    <span className="badge badge-amber" style={{ fontSize:'9px' }}>Confidence: {item.confidence}%</span>
                    <span style={{ fontSize:'10px', color:'var(--cyan)', fontWeight:600 }}>{item.company_name}</span>
                  </div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                    {ISSUE_LABELS[item.issue_type] || item.issue_type} · {fmtDateTime(item.created_at)}
                  </div>
                  {item.reasoning && (
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'4px', fontStyle:'italic' }}>
                      AI note: {item.reasoning}
                    </div>
                  )}
                </div>
              </div>

              {/* Draft message (editable) */}
              <div style={{ marginBottom:'12px' }}>
                <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>AI Draft — edit before sending:</label>
                <textarea className="input-field w-full" rows={4} style={{ fontSize:'13px', lineHeight:1.6, resize:'vertical' }}
                  value={editDraft?.id === item.id ? editDraft.message : item.draft_message}
                  onChange={e => setEditDraft({ id: item.id, message: e.target.value })}/>
              </div>

              <div style={{ display:'flex', gap:'8px' }}>
                <button className="btn btn-primary btn-sm" onClick={() => approveDraft(item.id)}>
                  <Send size={10}/> Send Reply
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => rejectDraft(item.id)}>
                  <X size={10}/> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Response Log tab ──────────────────────────────────────────────────── */}
      {tab === 'log' && (
        <div>
          {log.length === 0 ? (
            <div className="card" style={{ padding:'40px', textAlign:'center', color:'var(--text-muted)' }}>
              <div style={{ fontSize:'13px' }}>No AI activity yet. Enable AI support and it will appear here.</div>
            </div>
          ) : (
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Ticket</th><th>Company</th><th>Action</th><th>Confidence</th><th>Reasoning</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {log.map(entry => {
                    const as = ACTION_STYLES[entry.action] || { color:'var(--text-muted)', label: entry.action };
                    return (
                      <tr key={entry.id}>
                        <td>
                          <div style={{ fontWeight:600, fontSize:'12px' }}>#{entry.ticket_id}</div>
                          <div style={{ fontSize:'10px', color:'var(--text-muted)', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{entry.subject}</div>
                        </td>
                        <td style={{ fontSize:'12px', color:'var(--cyan)', fontWeight:600 }}>{entry.company_name}</td>
                        <td>
                          <span style={{ fontSize:'10px', fontWeight:700, color: as.color, background:`color-mix(in srgb, ${as.color} 13%, transparent)`, border:`1px solid color-mix(in srgb, ${as.color} 25%, transparent)`, borderRadius:'4px', padding:'2px 7px' }}>
                            {as.label}
                          </span>
                        </td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:'12px', color: entry.confidence >= 75 ? 'var(--green)' : 'var(--amber)' }}>
                          {entry.confidence != null ? entry.confidence + '%' : '—'}
                        </td>
                        <td style={{ fontSize:'11px', color:'var(--text-muted)', maxWidth:'220px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{entry.reasoning || '—'}</td>
                        <td style={{ fontSize:'11px', color:'var(--text-muted)' }}>{fmtDateTime(entry.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThemePage() {
  const [current, setCurrent] = useState(() => {
    const s = localStorage.getItem('ignitorads-theme');
    return (s === 'light' || s === 'dark') ? s : 'dark';
  });

  function applyTheme(id) {
    setCurrent(id);
    document.documentElement.setAttribute('data-theme', id);
    document.body.setAttribute('data-theme', id);
    localStorage.setItem('ignitorads-theme', id);
    toast.success(`Theme switched to ${THEMES.find(t=>t.id===id)?.label}`);
  }

  return (
    <div>
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, marginBottom:'16px' }}>Theme</h2>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'10px' }}>
        {THEMES.map(t => (
          <div key={t.id} onClick={()=>applyTheme(t.id)}
            style={{ padding:'16px', borderRadius:'var(--radius)', cursor:'pointer', border:`2px solid ${current===t.id?t.dot:'var(--border)'}`,
              background: current===t.id ? `color-mix(in srgb, ${t.dot} 8%, transparent)` : 'var(--bg-card)', transition:'all 0.15s' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' }}>
              <div style={{ width:20, height:20, borderRadius:'50%', background:t.dot, flexShrink:0 }}/>
              <div style={{ width:40, height:20, borderRadius:'4px', background:t.bg, border:'1px solid var(--border)' }}/>
            </div>
            <div style={{ fontWeight:700, fontSize:'13px', color: current===t.id ? t.dot : 'var(--text-primary)' }}>{t.label}</div>
            {current === t.id && <div style={{ fontSize:'10px', color:t.dot, marginTop:'2px' }}>✓ Active</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Admin Notification Bell ──────────────────────────────────────────────────
function AdminNotificationBell({ onNavigateSupport }) {
  const [open, setOpen]           = useState(false);
  const [notifs, setNotifs]       = useState([]);
  const [unreadCount, setUnread]  = useState(0);

  useEffect(() => {
    loadNotifs();
    const iv = setInterval(loadNotifs, 30000); // poll every 30s
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { if (open) loadNotifs(); }, [open]);

  async function loadNotifs() {
    try {
      const { data } = await axios.get('/api/admin/support-tickets/notifications/list', authH());
      setNotifs(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch {}
  }

  async function markAllRead() {
    try {
      await axios.patch('/api/admin/support-tickets/notifications/read', {}, authH());
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  }

  function fmtTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  }

  const TYPE_ICONS = { new_ticket: '🆕', ticket_reply: '💬' };
  const TYPE_COLORS = { new_ticket: 'var(--cyan)', ticket_reply: 'var(--purple)' };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: open ? 'var(--bg-elevated)' : 'transparent',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
        padding: '6px 8px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', position: 'relative'
      }}>
        <Bell size={14} style={{ color: unreadCount > 0 ? 'var(--amber)' : 'var(--text-muted)' }} />
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'var(--red)', color: 'var(--on-red)', fontSize: '9px', fontWeight: 800, borderRadius: '10px', padding: '1px 5px', minWidth: '16px', textAlign: 'center', lineHeight: '14px' }}>{unreadCount}</span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '36px', right: 0, width: '480px', zIndex: 999, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>
              🔔 Support Notifications
              {unreadCount > 0 && <span style={{ marginLeft: '6px', background: 'var(--red)', color: 'var(--on-red)', fontSize: '9px', borderRadius: '8px', padding: '1px 6px' }}>{unreadCount} new</span>}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {unreadCount > 0 && (
                <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--cyan)' }}>Mark all read</button>
              )}
              {onNavigateSupport && (
                <button onClick={() => { setOpen(false); onNavigateSupport(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--purple)' }}>View all →</button>
              )}
            </div>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎉</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No new notifications. Support tickets from companies will appear here.</div>
              </div>
            ) : (
              notifs.map(n => (
                <div key={n.id} onClick={() => { setOpen(false); if (onNavigateSupport) onNavigateSupport(); }}
                  style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: n.is_read ? undefined : 'color-mix(in srgb, var(--cyan) 4%, transparent)', display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <div style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{TYPE_ICONS[n.type] || '🔔'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.5, fontWeight: n.is_read ? 400 : 600 }}>{n.message}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', display: 'flex', gap: '8px' }}>
                      <span style={{ color: TYPE_COLORS[n.type], fontWeight: 600, textTransform: 'uppercase', fontSize: '9px' }}>{n.type === 'new_ticket' ? 'New Ticket' : 'Reply'}</span>
                      <span>Ticket #{n.ticket_id}</span>
                      <span>{fmtTime(n.created_at)}</span>
                    </div>
                  </div>
                  {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--cyan)', flexShrink: 0, marginTop: '5px' }} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────
// ─── CTV Database page ───────────────────────────────────────────────────────
// Global CTV app reference data (store ID → developer domain) powering the
// Crawler and Bundle Tracker for Roku / Fire TV / Samsung / LG / Vizio /
// Android TV / tvOS. Import = upsert on (platform, store_id): re-importing
// refreshes rows, never duplicates.
// ─── Mobile Apps DB page ──────────────────────────────────────────────────────
// Global mobile app reference data (bundle_id + store → developer domain) that
// powers the Crawler / Watch Tower / Bundle Tracker read-through cache. Verified
// rows are served before any live store call; unverified rows are a worklist.
function MobileAppsDbPage() {
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('file');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [harvesting, setHarvesting] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [jumpInput, setJumpInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [rowsBusy, setRowsBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString(undefined, { year:'numeric', month:'short', day:'numeric' }) : '—';

  async function loadStats() {
    try { const { data } = await axios.get('/api/admin/mobile-apps/stats', authH()); setStats(data); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to load stats'); }
  }
  useEffect(() => { loadStats(); }, []);

  const [sortCol, setSortCol] = useState('app_name');
  const [sortDir, setSortDir] = useState('asc');
  async function loadRows(page = rowsPage, search = searchQ, store = storeFilter, state = stateFilter, limit = rowsPerPage, sort = sortCol, dir = sortDir) {
    setRowsBusy(true);
    try {
      const { data } = await axios.get('/api/admin/mobile-apps/apps', {
        ...authH(), params: { search, store, verify_state: state, page, limit, sort, dir },
      });
      setRows(data.rows); setRowsTotal(data.total); setRowsPage(data.page); setEditingId(null);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load rows'); }
    finally { setRowsBusy(false); }
  }
  useEffect(() => { loadRows(0); }, []);

  function toggleSort(col) {
    const nextDir = (sortCol === col && sortDir === 'asc') ? 'desc' : 'asc';
    setSortCol(col); setSortDir(nextDir);
    loadRows(0, searchQ, storeFilter, stateFilter, rowsPerPage, col, nextDir);
  }
  const sortableTh = (label, col, extra = {}) => (
    <th onClick={() => toggleSort(col)} title={`Sort by ${label}`}
        style={{ padding:'6px 8px', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', ...extra }}>
      {label}{sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  function startEdit(r) {
    setEditingId(r.id);
    setDraft({ app_name: r.app_name || '', developer_domain: r.developer_domain || '', store_url: r.store_url || '', category: r.category || '', verify_state: r.verify_state || 'unverified' });
  }
  async function saveEdit(r) {
    try {
      const { data } = await axios.put(`/api/admin/mobile-apps/apps/${r.id}`, draft, authH());
      setRows(prev => prev.map(x => x.id === r.id ? data.row : x));
      setEditingId(null); toast.success('Row updated'); loadStats();
    } catch (e) { toast.error(e.response?.data?.error || 'Update failed'); }
  }
  async function deleteRow(r) {
    if (!window.confirm(`Delete ${r.store} / ${r.bundle_id} ("${r.app_name || 'unnamed'}") from the mobile apps DB?`)) return;
    try {
      await axios.delete(`/api/admin/mobile-apps/apps/${r.id}`, authH());
      setRows(prev => prev.filter(x => x.id !== r.id)); setRowsTotal(t => t - 1);
      loadStats(); toast.success('Row deleted');
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  }

  function onFilePicked(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => toast.error('Could not read the file');
    reader.readAsText(f);
  }

  function downloadTemplate() {
    const csv = [
      'bundle_id,store,app_name,developer_domain,store_url,category',
      'com.example.puzzle,android,Example Puzzle,example.com,https://play.google.com/store/apps/details?id=com.example.puzzle,Puzzle',
      '284882215,ios,Example iOS App,example.com,https://apps.apple.com/us/app/id284882215,',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'mobile_apps_template.csv';
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  }

  // Chunked import — splits the CSV client-side so a 100k+ row file uploads as
  // many small POSTs with a progress bar (no request timeout, no huge payload).
  async function runImport() {
    setBusy(true); setLastResult(null); setProgress(null);
    try {
      if (mode === 'url') {
        if (!/^https?:\/\//i.test(url)) { toast.error('Enter a valid http(s) URL'); setBusy(false); return; }
        const { data } = await axios.post('/api/admin/mobile-apps/import-url', { url }, authH());
        setLastResult(data); setStats(data.stats);
        toast.success(`Imported: ${(data.inserted || 0).toLocaleString()} new, ${(data.skipped || 0).toLocaleString()} skipped`);
        loadRows(0); setUrl(''); setBusy(false); return;
      }
      const text = csvText.trim();
      if (!text) { toast.error(mode === 'file' ? 'Choose a CSV file first' : 'Paste CSV content first'); setBusy(false); return; }
      const lines = text.split(/\r?\n/);
      const header = lines[0];
      const body = lines.slice(1).filter(l => l.trim() !== '');
      const CHUNK = 8000;
      let inserted = 0, skipped = 0, processed = 0;
      setProgress({ done: 0, total: body.length });
      for (let k = 0; k < body.length; k += CHUNK) {
        const part = [header, ...body.slice(k, k + CHUNK)].join('\n');
        const { data } = await axios.post('/api/admin/mobile-apps/import', { csvText: part }, authH());
        inserted += data.inserted || 0; skipped += data.skipped || 0; processed += data.processed || 0;
        setProgress({ done: Math.min(k + CHUNK, body.length), total: body.length });
      }
      setLastResult({ processed, inserted, skipped });
      toast.success(`Imported: ${inserted.toLocaleString()} new, ${skipped.toLocaleString()} skipped`);
      loadStats(); loadRows(0); setCsvText(''); setFileName('');
    } catch (e) {
      const d = e.response?.data; setLastResult(d || { error: e.message });
      toast.error(d?.error || 'Import failed');
    } finally { setBusy(false); setProgress(null); }
  }

  async function runHarvest() {
    if (!window.confirm('Seed the master DB from already-resolved domains in the crawler cache? Existing rows are kept — this only adds verified rows.')) return;
    setHarvesting(true);
    try {
      const { data } = await axios.post('/api/admin/mobile-apps/harvest', {}, authH());
      setStats(data.stats);
      toast.success(`Harvested ${(data.inserted || 0).toLocaleString()} verified rows · ${(data.total || 0).toLocaleString()} total`);
      loadRows(0);
    } catch (e) { toast.error(e.response?.data?.error || 'Harvest failed'); }
    finally { setHarvesting(false); }
  }

  async function runRecheckRested() {
    if (!window.confirm('Re-open the rested tail — unverified rows that hit max attempts on a transient/blocked store response — so the verify worker retries them on the next tick?')) return;
    setRechecking(true);
    try {
      const { data } = await axios.post('/api/admin/mobile-apps/recheck-rested', {}, authH());
      setStats(data.stats);
      toast.success(`Re-opened ${(data.reset || 0).toLocaleString()} rested row${data.reset === 1 ? '' : 's'} for another pass`);
      loadRows(0);
    } catch (e) { toast.error(e.response?.data?.error || 'Re-check failed'); }
    finally { setRechecking(false); }
  }

  function goToPage() {
    const n = parseInt(jumpInput, 10);
    if (n >= 1 && n <= totalPages) { loadRows(n - 1); setJumpInput(''); }
    else toast.error(`Enter a page between 1 and ${totalPages}`);
  }

  const MODES = [
    { id:'file',  label:'Upload CSV', icon: Upload },
    { id:'paste', label:'Paste CSV',  icon: ClipboardList },
    { id:'url',   label:'From URL',   icon: Link2 },
  ];
  const STATE_COLOR = { verified:'var(--green)', no_domain:'var(--amber)', unverified:'var(--text-muted)', removed:'var(--red)' };
  const totalPages = Math.max(1, Math.ceil(rowsTotal / rowsPerPage));

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
        <div>
          <div style={{ fontSize:'16px', fontWeight:700, color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'8px' }}>
            <Smartphone size={16} style={{ color:'var(--cyan)' }}/> Mobile Apps Database
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>
            Global bundle → developer-domain reference. Verified rows are served from this DB before any live store call (read-through), across all companies.
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={loadStats} disabled={busy || harvesting}><RefreshCw size={12}/> Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={runHarvest} disabled={busy || harvesting}>
            <Database size={12}/> {harvesting ? 'Harvesting…' : 'Harvest from cache'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={runRecheckRested} disabled={busy || harvesting || rechecking} title="Reset attempts on rows that hit max retries so the verify worker tries them again">
            <RefreshCw size={12} style={{ animation: rechecking ? 'spin 1s linear infinite' : 'none' }}/> {rechecking ? 'Re-checking…' : 'Re-check rested'}
          </button>
        </div>
      </div>

      {/* Coverage */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div className="card-title" style={{ marginBottom:'10px' }}>Coverage</div>
        {!stats ? (
          <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>Loading…</div>
        ) : (
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:'13px', fontWeight:800, color:'var(--cyan)', marginRight:'4px' }}>
              {(stats.total || 0).toLocaleString()} apps
            </span>
            {Object.entries(stats.byStore || {}).map(([k, v]) => (
              <span key={k} className="badge badge-muted" style={{ fontSize:'10px' }}>{k} · {v.toLocaleString()}</span>
            ))}
            <span style={{ width:'1px', height:'14px', background:'var(--border)', margin:'0 4px' }} />
            {['verified', 'no_domain', 'unverified', 'removed'].map(k => (
              <span key={k} className="badge badge-muted" style={{ fontSize:'10px', color: STATE_COLOR[k] }}>
                {k} · {((stats.byState || {})[k] || 0).toLocaleString()}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Import */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
          <div className="card-title">Add or update data</div>
          <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}><Download size={12}/> Download CSV template</button>
        </div>
        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'12px' }}>
          Required: <strong>bundle_id</strong>. Optional: store · app_name · developer_domain (bare host, no http) · store_url · category.
          Store is auto-detected from the URL or bundle format. New rows import as <em>unverified</em>; re-imports never overwrite an existing row.
        </div>

        <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
          {MODES.map(({ id, label, icon: Icon }) => (
            <button key={id} className={mode === id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setMode(id)}>
              <Icon size={12}/> {label}
            </button>
          ))}
        </div>

        {mode === 'file' && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer' }}>
              <Upload size={12}/> Choose CSV file
              <input type="file" accept=".csv,text/csv" onChange={onFilePicked} style={{ display:'none' }}/>
            </label>
            <span style={{ fontSize:'11px', color: fileName ? 'var(--text-primary)' : 'var(--text-muted)' }}>{fileName || 'No file chosen'}</span>
          </div>
        )}
        {mode === 'paste' && (
          <textarea className="input-field w-full" rows={8}
            placeholder={'bundle_id,store,app_name,developer_domain,store_url,category\ncom.foo.bar,android,Foo,foo.com,https://play.google.com/store/apps/details?id=com.foo.bar,Puzzle'}
            value={csvText} onChange={e => setCsvText(e.target.value)} style={{ fontFamily:'var(--font-mono)', fontSize:'11px' }}/>
        )}
        {mode === 'url' && (
          <input className="input-field w-full" placeholder="https://example.com/mobile-apps.csv" value={url} onChange={e => setUrl(e.target.value)}/>
        )}

        {progress && (
          <div style={{ marginTop:'12px' }}>
            <div style={{ height:'6px', background:'var(--border)', borderRadius:'3px', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${Math.round(progress.done / Math.max(progress.total, 1) * 100)}%`, background:'var(--cyan)', transition:'width 0.2s' }}/>
            </div>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'4px' }}>
              Uploading {progress.done.toLocaleString()} / {progress.total.toLocaleString()} rows…
            </div>
          </div>
        )}

        <div style={{ marginTop:'12px' }}>
          <button className="btn btn-primary" onClick={runImport} disabled={busy}>{busy ? 'Importing…' : 'Import'}</button>
          {lastResult && !busy && lastResult.inserted != null && (
            <span style={{ marginLeft:'10px', fontSize:'11px', color:'var(--text-muted)' }}>
              Last: {Number(lastResult.inserted).toLocaleString()} new, {Number(lastResult.skipped || 0).toLocaleString()} skipped
            </span>
          )}
        </div>
      </div>

      {/* Browse & edit */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div className="card-title" style={{ marginBottom:'10px' }}>Browse & edit</div>
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'center', flexWrap:'wrap' }}>
          <input className="input-field" placeholder="Search bundle, app name, or domain…"
            value={searchQ} onChange={e => setSearchQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') loadRows(0); }}
            style={{ maxWidth:'300px', fontSize:'12px' }}/>
          <select className="input-field" value={storeFilter} onChange={e => { setStoreFilter(e.target.value); loadRows(0, searchQ, e.target.value, stateFilter); }} style={{ maxWidth:'140px', fontSize:'12px' }}>
            <option value="">All stores</option>
            {['ios', 'android', 'unknown'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="input-field" value={stateFilter} onChange={e => { setStateFilter(e.target.value); loadRows(0, searchQ, storeFilter, e.target.value); }} style={{ maxWidth:'150px', fontSize:'12px' }}>
            <option value="">All states</option>
            {['verified', 'no_domain', 'unverified', 'removed'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => loadRows(0)} disabled={rowsBusy}><Search size={12}/> Search</button>
          <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--text-muted)' }}>{rowsTotal.toLocaleString()} row{rowsTotal !== 1 ? 's' : ''}</span>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr style={{ textAlign:'left', color:'var(--text-muted)', fontSize:'10px', textTransform:'uppercase' }}>
                {sortableTh('App / bundle', 'app_name')}
                {sortableTh('Store', 'store')}
                {sortableTh('Developer domain', 'developer_domain')}
                {sortableTh('Privacy policy', 'privacy_policy_domain')}
                {sortableTh('State', 'verify_state')}
                {sortableTh('Category', 'category')}
                {sortableTh('Verified', 'last_verified_at')}
                {sortableTh('Added', 'created_at')}
                {sortableTh('Modified', 'updated_at')}
                <th style={{ padding:'6px 8px', textAlign:'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !rowsBusy && (
                <tr><td colSpan={10} style={{ padding:'16px 8px', color:'var(--text-muted)', fontSize:'11px' }}>No rows. Harvest from cache or import a file to populate the database.</td></tr>
              )}
              {rows.map(r => editingId === r.id ? (
                <tr key={r.id} style={{ borderTop:'1px solid var(--border)', background:'var(--bg-subtle)' }}>
                  <td style={{ padding:'6px 8px' }}>
                    <input className="input-field" value={draft.app_name} onChange={e => setDraft({ ...draft, app_name: e.target.value })} placeholder="app name" style={{ fontSize:'11px', width:'100%' }}/>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>{r.bundle_id}</div>
                  </td>
                  <td style={{ padding:'6px 8px' }}><span className="badge badge-muted" style={{ fontSize:'10px' }}>{r.store}</span></td>
                  <td style={{ padding:'6px 8px' }}><input className="input-field" value={draft.developer_domain} onChange={e => setDraft({ ...draft, developer_domain: e.target.value })} placeholder="example.com" style={{ fontSize:'11px', width:'100%' }}/></td>
                  <td style={{ padding:'6px 8px', fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--text-muted)' }}>{r.privacy_policy_domain || '—'}</td>
                  <td style={{ padding:'6px 8px' }}>
                    <select className="input-field" value={draft.verify_state} onChange={e => setDraft({ ...draft, verify_state: e.target.value })} style={{ fontSize:'11px' }}>
                      {['verified', 'no_domain', 'unverified', 'removed'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:'6px 8px' }}><input className="input-field" value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} style={{ fontSize:'11px', width:'100%' }}/></td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)' }} title={r.last_verified_at || ''}>{fmtDate(r.last_verified_at)}</td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)' }} title={r.created_at || ''}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)' }} title={r.updated_at || ''}>{fmtDate(r.updated_at)}</td>
                  <td style={{ padding:'6px 8px', textAlign:'right', whiteSpace:'nowrap' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => saveEdit(r)}><Save size={12}/></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)} style={{ marginLeft:'4px' }}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'6px 8px' }}>
                    <div style={{ fontWeight:600 }}>{r.app_name || <span style={{ color:'var(--text-muted)' }}>—</span>}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:'10px', color:'var(--text-muted)' }}>{r.bundle_id}</div>
                  </td>
                  <td style={{ padding:'6px 8px' }}>
                    {r.store_url
                      ? <a href={r.store_url} target="_blank" rel="noreferrer" title="Open store listing" style={{ textDecoration:'none' }}><span className="badge badge-muted" style={{ fontSize:'10px', cursor:'pointer', color:'var(--cyan)' }}>{r.store} <ExternalLink size={9} style={{ verticalAlign:'-1px' }}/></span></a>
                      : <span className="badge badge-muted" style={{ fontSize:'10px' }}>{r.store}</span>}
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:'var(--font-mono)', fontSize:'11px' }}>
                    {r.developer_domain
                      ? <a href={`https://${r.developer_domain}/app-ads.txt`} target="_blank" rel="noreferrer" title="Open app-ads.txt" style={{ color:'var(--cyan)', textDecoration:'none' }}>{r.developer_domain}</a>
                      : <span style={{ color:'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:'var(--font-mono)', fontSize:'11px' }}>
                    {r.privacy_policy_domain
                      ? <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                          <a href={`https://${r.privacy_policy_domain}/app-ads.txt`} target="_blank" rel="noreferrer" title="Open app-ads.txt" style={{ color:'var(--cyan)', textDecoration:'none' }}>{r.privacy_policy_domain}</a>
                          {r.resolved_via_privacy && <span title="app-ads.txt was found on the privacy-policy domain, not the developer domain" style={{ fontSize:'8px', fontWeight:700, color:'var(--cyan)', border:'1px solid var(--cyan)', borderRadius:'3px', padding:'0 4px', alignSelf:'flex-start', whiteSpace:'nowrap' }}>app-ads.txt found here</span>}
                        </div>
                      : <span style={{ color:'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ padding:'6px 8px' }}><span style={{ fontSize:'11px', fontWeight:600, color: STATE_COLOR[r.verify_state] || 'var(--text-muted)' }}>{r.verify_state}</span></td>
                  <td style={{ padding:'6px 8px', color:'var(--text-muted)' }}>{r.category || '—'}</td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)', whiteSpace:'nowrap' }} title={r.last_verified_at || ''}>{fmtDate(r.last_verified_at)}</td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)', whiteSpace:'nowrap' }} title={r.created_at || ''}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding:'6px 8px', fontSize:'11px', color:'var(--text-muted)', whiteSpace:'nowrap' }} title={r.updated_at || ''}>{fmtDate(r.updated_at)}</td>
                  <td style={{ padding:'6px 8px', textAlign:'right', whiteSpace:'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => startEdit(r)}><Edit2 size={12}/></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => deleteRow(r)} style={{ marginLeft:'4px' }}><Trash2 size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rowsTotal > 0 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', marginTop:'12px', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'var(--text-muted)' }}>
              Rows per page
              <select className="input-field" value={rowsPerPage}
                onChange={e => { const n = parseInt(e.target.value, 10); setRowsPerPage(n); loadRows(0, searchQ, storeFilter, stateFilter, n); }}
                style={{ fontSize:'11px', padding:'2px 6px', maxWidth:'80px' }}>
                {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
              <button className="btn btn-secondary btn-sm" disabled={rowsPage <= 0 || rowsBusy} onClick={() => loadRows(rowsPage - 1)}>Prev</button>
              <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>Page {rowsPage + 1} / {totalPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={rowsPage + 1 >= totalPages || rowsBusy} onClick={() => loadRows(rowsPage + 1)}>Next</button>
              <input className="input-field" type="number" min={1} max={totalPages} placeholder="Go to"
                value={jumpInput} onChange={e => setJumpInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') goToPage(); }}
                style={{ width:'72px', fontSize:'11px' }}/>
              <button className="btn btn-secondary btn-sm" disabled={rowsBusy || !jumpInput} onClick={goToPage}>Go</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CtvDatabasePage() {
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('file');           // file | paste | url
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const ROWS_PER_PAGE = 25;
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [platFilter, setPlatFilter] = useState('');
  const [rowsBusy, setRowsBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  async function loadStats() {
    try {
      const { data } = await axios.get('/api/admin/ctv/stats', authH());
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load CTV stats');
    }
  }
  useEffect(() => { loadStats(); }, []);

  async function loadRows(page = rowsPage, search = searchQ, platform = platFilter) {
    setRowsBusy(true);
    try {
      const { data } = await axios.get('/api/admin/ctv/apps', {
        ...authH(), params: { search, platform, page, limit: ROWS_PER_PAGE },
      });
      setRows(data.rows); setRowsTotal(data.total); setRowsPage(data.page);
      setEditingId(null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load rows');
    } finally { setRowsBusy(false); }
  }
  useEffect(() => { loadRows(0); }, []);

  function startEdit(r) {
    setEditingId(r.id);
    setDraft({ name: r.name || '', developer: r.developer || '', developer_domain: r.developer_domain || '', store_url: r.store_url || '', category: r.category || '' });
  }
  async function saveEdit(r) {
    try {
      const { data } = await axios.put(`/api/admin/ctv/apps/${r.id}`, draft, authH());
      setRows(prev => prev.map(x => x.id === r.id ? data.row : x));
      setEditingId(null);
      toast.success('Row updated');
    } catch (e) { toast.error(e.response?.data?.error || 'Update failed'); }
  }
  async function deleteRow(r) {
    if (!window.confirm(`Delete ${r.platform} / ${r.store_id} ("${r.name || 'unnamed'}") from the CTV database?`)) return;
    try {
      await axios.delete(`/api/admin/ctv/apps/${r.id}`, authH());
      setRows(prev => prev.filter(x => x.id !== r.id));
      setRowsTotal(tot => tot - 1);
      loadStats();
      toast.success('Row deleted');
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  }

  function onFilePicked(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => toast.error('Could not read the file');
    reader.readAsText(f);
  }

  async function runImport() {
    setBusy(true); setLastResult(null);
    try {
      let data;
      if (mode === 'url') {
        if (!/^https?:\/\//i.test(url)) { toast.error('Enter a valid http(s) URL'); setBusy(false); return; }
        ({ data } = await axios.post('/api/admin/ctv/import-url', { url }, authH()));
      } else {
        if (!csvText.trim()) { toast.error(mode === 'file' ? 'Choose a CSV file first' : 'Paste CSV content first'); setBusy(false); return; }
        ({ data } = await axios.post('/api/admin/ctv/import', { csvText }, authH()));
      }
      setLastResult(data);
      setStats(data.stats);
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated`);
      loadRows(0);
      setCsvText(''); setFileName(''); setUrl('');
    } catch (e) {
      const d = e.response?.data;
      setLastResult(d || { error: e.message });
      toast.error(d?.error || 'Import failed');
    } finally { setBusy(false); }
  }

  async function reloadSeed() {
    if (!window.confirm('Re-import the bundled seed file? Existing rows with the same platform + store ID are refreshed; nothing is deleted.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post('/api/admin/ctv/seed', {}, authH());
      setLastResult(data); setStats(data.stats);
      toast.success(`Seed reloaded: ${data.inserted} new, ${data.updated} updated`);
      loadRows(0);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Seed reload failed');
    } finally { setBusy(false); }
  }

  const MODES = [
    { id:'file',  label:'Upload CSV',  icon: Upload },
    { id:'paste', label:'Paste CSV',   icon: ClipboardList },
    { id:'url',   label:'From URL',    icon: Link2 },
  ];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
        <div>
          <div style={{ fontSize:'16px', fontWeight:700, color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'8px' }}>
            <Database size={16} style={{ color:'var(--cyan)' }}/> CTV App Database
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>
            Global reference data mapping CTV store IDs to developer domains — used by the Crawler and Bundle Tracker across all companies.
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={loadStats} disabled={busy}><RefreshCw size={12}/> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={reloadSeed} disabled={busy}><Database size={12}/> Reload bundled seed</button>
        </div>
      </div>

      {/* Stats */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div className="card-title" style={{ marginBottom:'10px' }}>Coverage</div>
        {!stats ? (
          <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>Loading…</div>
        ) : (
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:'13px', fontWeight:800, color:'var(--cyan)', marginRight:'8px' }}>
              {stats.total.toLocaleString()} apps
            </span>
            {stats.platforms.map(p => (
              <span key={p.platform} className="badge badge-muted" style={{ fontSize:'10px' }}>
                {p.platform} · {p.count.toLocaleString()}
              </span>
            ))}
            {stats.platforms[0]?.last_updated && (
              <span style={{ fontSize:'10px', color:'var(--text-muted)', marginLeft:'auto' }}>
                Last updated: {new Date(Math.max(...stats.platforms.map(p => new Date(p.last_updated)))).toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Import */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div className="card-title" style={{ marginBottom:'4px' }}>Add or update data</div>
        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'12px' }}>
          Accepted columns: platform / source_type · StoreId · AppAdsUrl / domain · Name · Developer · StoreUrl · Category.
          Pivot-style exports (blank platform/domain cells meaning "same as above") are handled. Re-imports refresh matching rows.
        </div>

        <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
          {MODES.map(({ id, label, icon: Icon }) => (
            <button key={id} className={mode === id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              onClick={() => setMode(id)}>
              <Icon size={12}/> {label}
            </button>
          ))}
        </div>

        {mode === 'file' && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer' }}>
              <Upload size={12}/> Choose CSV file
              <input type="file" accept=".csv,text/csv" onChange={onFilePicked} style={{ display:'none' }}/>
            </label>
            <span style={{ fontSize:'11px', color: fileName ? 'var(--text-primary)' : 'var(--text-muted)' }}>
              {fileName || 'No file chosen'}
            </span>
          </div>
        )}
        {mode === 'paste' && (
          <textarea className="input-field w-full" rows={8} placeholder={'platform,StoreId,AppAdsUrl,Name,Developer,StoreUrl\nroku,90566,10news.com,ABC 10 News,Scripps,https://channelstore.roku.com/details/90566'}
            value={csvText} onChange={e => setCsvText(e.target.value)}
            style={{ fontFamily:'var(--font-mono)', fontSize:'11px' }}/>
        )}
        {mode === 'url' && (
          <input className="input-field w-full" placeholder="https://example.com/ctv-apps.csv"
            value={url} onChange={e => setUrl(e.target.value)}/>
        )}

        <div style={{ marginTop:'12px' }}>
          <button className="btn btn-primary" onClick={runImport} disabled={busy}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>

      {/* Last import result */}
      {/* Browse & edit */}
      <div className="card" style={{ marginBottom:'16px' }}>
        <div className="card-title" style={{ marginBottom:'10px' }}>Browse & edit</div>
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'center' }}>
          <input className="input-field" placeholder="Search store ID, name, developer, or domain…"
            value={searchQ} onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') loadRows(0); }}
            style={{ maxWidth:'320px', fontSize:'12px' }}/>
          <select className="input-field" value={platFilter}
            onChange={e => { setPlatFilter(e.target.value); loadRows(0, searchQ, e.target.value); }}
            style={{ maxWidth:'150px', fontSize:'12px' }}>
            <option value="">All platforms</option>
            {['roku','firetv','samsung','lg','vizio','androidtv','tvos'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => loadRows(0)} disabled={rowsBusy}>Search</button>
          <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--text-muted)' }}>
            {rowsTotal.toLocaleString()} row{rowsTotal !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
            <thead>
              <tr style={{ color:'var(--text-muted)', textAlign:'left', textTransform:'uppercase', fontSize:'9px', letterSpacing:'0.5px' }}>
                <th style={{ padding:'6px 8px' }}>Platform</th>
                <th style={{ padding:'6px 8px' }}>Store ID</th>
                <th style={{ padding:'6px 8px' }}>Name</th>
                <th style={{ padding:'6px 8px' }}>Developer</th>
                <th style={{ padding:'6px 8px' }}>Domain</th>
                <th style={{ padding:'6px 8px' }}>Category</th>
                <th style={{ padding:'6px 8px' }}>Store URL</th>
                <th style={{ padding:'6px 8px', width:'70px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'6px 8px' }}><span className="badge badge-muted" style={{ fontSize:'9px' }}>{r.platform}</span></td>
                  <td style={{ padding:'6px 8px', fontFamily:'var(--font-mono)', maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-primary)' }} title={r.store_id}>{r.store_id}</td>
                  {editingId === r.id ? (
                    <>
                      <td style={{ padding:'4px' }}><input className="input-field" value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}/></td>
                      <td style={{ padding:'4px' }}><input className="input-field" value={draft.developer} onChange={e=>setDraft(d=>({...d,developer:e.target.value}))} style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}/></td>
                      <td style={{ padding:'4px' }}><input className="input-field" value={draft.developer_domain} onChange={e=>setDraft(d=>({...d,developer_domain:e.target.value}))} style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}/></td>
                      <td style={{ padding:'4px' }}><input className="input-field" value={draft.category} onChange={e=>setDraft(d=>({...d,category:e.target.value}))} style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}/></td>
                      <td style={{ padding:'4px' }}><input className="input-field" value={draft.store_url} onChange={e=>setDraft(d=>({...d,store_url:e.target.value}))} placeholder="https://…" style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}/></td>
                      <td style={{ padding:'4px', whiteSpace:'nowrap' }}>
                        <button className="btn btn-primary btn-sm" title="Save" onClick={() => saveEdit(r)} style={{ marginRight:'4px' }}><CheckCircle size={11}/></button>
                        <button className="btn btn-secondary btn-sm" title="Cancel" onClick={() => setEditingId(null)}><X size={11}/></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding:'6px 8px', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-primary)', fontWeight:600 }} title={r.name || ''}>{r.name || '—'}</td>
                      <td style={{ padding:'6px 8px', maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-primary)' }} title={r.developer || ''}>{r.developer || '—'}</td>
                      <td style={{ padding:'6px 8px', color:'var(--cyan)' }}>{r.developer_domain}</td>
                      <td style={{ padding:'6px 8px', color:'var(--text-primary)' }}>{r.category || '—'}</td>
                      <td style={{ padding:'6px 8px', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.store_url || ''}>
                        {r.store_url ? (
                          <a href={r.store_url} target="_blank" rel="noreferrer" style={{ color:'var(--cyan)', display:'inline-flex', alignItems:'center', gap:'4px', textDecoration:'none' }}>
                            <ExternalLink size={10}/> {r.store_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 28)}
                          </a>
                        ) : '—'}
                      </td>
                      <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                        <button title="Edit row" onClick={() => startEdit(r)}
                          style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', color:'var(--text-secondary)', padding:'4px 7px', marginRight:'4px' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--cyan)'; e.currentTarget.style.borderColor = 'var(--cyan)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                          <Edit2 size={12}/>
                        </button>
                        <button title="Delete row" onClick={() => deleteRow(r)}
                          style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', color:'var(--text-secondary)', padding:'4px 7px' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.borderColor = 'var(--red)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                          <Trash2 size={12}/>
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {rows.length === 0 && !rowsBusy && (
                <tr><td colSpan={8} style={{ padding:'16px', textAlign:'center', fontSize:'11px', color:'var(--text-muted)' }}>No rows match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'10px' }}>
          <button className="btn btn-secondary btn-sm" disabled={rowsPage === 0 || rowsBusy} onClick={() => loadRows(rowsPage - 1)}>← Prev</button>
          <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
            Page {rowsPage + 1} of {Math.max(1, Math.ceil(rowsTotal / ROWS_PER_PAGE))}
          </span>
          <button className="btn btn-secondary btn-sm" disabled={(rowsPage + 1) * ROWS_PER_PAGE >= rowsTotal || rowsBusy} onClick={() => loadRows(rowsPage + 1)}>Next →</button>
        </div>
      </div>

      {lastResult && (
        <div className="card">
          <div className="card-title" style={{ marginBottom:'8px' }}>Last import result</div>
          {lastResult.error ? (
            <div style={{ fontSize:'12px', color:'var(--red)' }}>
              {lastResult.error}
              {Array.isArray(lastResult.details) && lastResult.details.map((d, i) => (
                <div key={i} style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'4px' }}>· {d}</div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize:'12px', color:'var(--text-secondary)' }}>
              Processed <strong style={{ color:'var(--text-primary)' }}>{lastResult.processed?.toLocaleString()}</strong> rows ·{' '}
              <strong style={{ color:'var(--green)' }}>{lastResult.inserted?.toLocaleString()} new</strong> ·{' '}
              <strong style={{ color:'var(--cyan)' }}>{lastResult.updated?.toLocaleString()} updated</strong>
              {Array.isArray(lastResult.warnings) && lastResult.warnings.length > 0 && lastResult.warnings.map((w, i) => (
                <div key={i} style={{ fontSize:'11px', color:'var(--amber)', marginTop:'4px' }}>⚠ {w}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [adminUser, setAdminUser] = useState(() => {
    const token = getAdminToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) { setAdminToken(null); return null; }
      return payload;
    } catch { return null; }
  });
  const [activePage, setActivePage] = useState('companies');
  const [companies, setCompanies]   = useState([]);
  const [stats, setStats]           = useState(null);
  const [toolPrices, setToolPrices] = useState({});
  const [loading, setLoading]       = useState(false);
  const [searchQ, setSearchQ]       = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, p] = await Promise.all([
        axios.get('/api/admin/companies', authH()),
        axios.get('/api/admin/stats', authH()),
        axios.get('/api/admin/tool-pricing', authH()),
      ]);
      setCompanies(c.data.filter(co => co.slug !== '__tool_pricing__'));
      setStats(s.data);
      const priceMap = {};
      (p.data || []).forEach(row => { priceMap[row.tool_path] = parseFloat(row.price_usd) || 0; });
      setToolPrices(priceMap);
    } catch (err) {
      if (err.response?.status === 401) { setAdminToken(null); setAdminUser(null); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
    // loadData never touches adminUser — prevents any re-render cascade
  }, []);

  // Load data once on mount
  useEffect(() => { if (adminUser) loadData(); }, []);

  // Fetch role from DB once on mount — completely separate from data loading
  // so it never interferes with companies/stats state
  useEffect(() => {
    if (!adminUser) return;
    axios.get('/api/admin/me', authH())
      .then(({ data }) => {
        if (!data) return;
        setAdminUser(prev => {
          const changed = Object.keys(data).some(k => prev[k] !== data[k]);
          return changed ? { ...prev, ...data } : prev;
        });
      })
      .catch(() => {}); // silent — role falls back to token value
  }, []); // runs once only

  if (!adminUser) return <AdminLogin onLoggedIn={user => { setAdminUser(user); loadData(); }} />;

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(searchQ.toLowerCase()) ||
    c.slug.includes(searchQ.toLowerCase())
  );

  const ALL_NAV = [
    { id:'companies',  label:'Companies',    icon: Building2  },
    { id:'pricing',    label:'Tool Pricing', icon: Package    },
    { id:'engine',     label:'Engine Settings', icon: Settings },
    { id:'finance',    label:'Finance',      icon: CreditCard },
    { id:'changelog',  label:'Change Log',   icon: Clock      },
    { id:'admins',     label:'Admin Users',  icon: UserCheck  },
    { id:'support',    label:'Support',      icon: FileText   },
    { id:'ai-support', label:'AI Support',   icon: Bot        },
    { id:'news',       label:'Industry Pulse',icon: Newspaper  },
    { id:'ctv',        label:'CTV Database', icon: Database   },
    { id:'mobileapps', label:'Mobile Apps DB', icon: Smartphone },
    { id:'sellersjson',label:'Sellers.json DB', icon: Database   },
    { id:'theme',      label:'Theme',        icon: Palette    },
  ];
  const NAV = ALL_NAV.filter(n => canAccessPage(adminUser, n.id));

  return (
    <div style={{ flex:1, minHeight:0, background:'var(--bg-base)', display:'flex' }}>
      {/* Sidebar */}
      <div style={{ width:'200px', flexShrink:0, background:'var(--bg-card)', borderRight:'1px solid var(--border)', padding:'16px 0', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'0 16px 16px', borderBottom:'1px solid var(--border)', marginBottom:'8px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
            <img src="/brand/icon-192.png" alt="IgnitorAds" style={{ width:28, height:28, objectFit:'contain', borderRadius:'6px', flexShrink:0 }}/>
            <div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'13px', fontWeight:700, letterSpacing:'-0.01em', color:'var(--cyan)', lineHeight:1 }}>IgnitorAds</div>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:'9px', color:'var(--text-muted)', letterSpacing:'0.5px', marginTop:'1px' }}>ADMIN PANEL</div>
            </div>
          </div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{adminUser.email}</div>
          <div style={{ marginTop:'4px' }}>
            <span style={{ fontSize:'9px', fontWeight:700, color: getRoleInfo(adminUser?.role||'admin').color, background:`color-mix(in srgb, ${getRoleInfo(adminUser?.role||'admin').color} 13%, transparent)`, border:`1px solid color-mix(in srgb, ${getRoleInfo(adminUser?.role||'admin').color} 25%, transparent)`, borderRadius:'3px', padding:'1px 6px', textTransform:'uppercase' }}>
              {adminUser?.is_super ? 'Super Admin' : getRoleInfo(adminUser?.role||'admin').label}
            </span>
          </div>
        </div>
        {NAV.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={()=>setActivePage(id)}
            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', border:'none', cursor:'pointer', textAlign:'left', fontSize:'13px', fontWeight: activePage===id ? 700 : 400,
              fontFamily:'var(--font-mono)',
              background: activePage===id ? 'color-mix(in srgb, var(--cyan) 10%, transparent)' : 'transparent',
              color: activePage===id ? 'var(--cyan)' : 'var(--text-secondary)',
              borderLeft: activePage===id ? '2px solid var(--cyan)' : '2px solid transparent' }}>
            <Icon size={14}/> {label}
          </button>
        ))}
        <div style={{ marginTop:'auto', padding:'12px 16px', borderTop:'1px solid var(--border)' }}>
          <button className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'center' }}
            onClick={()=>{ setAdminToken(null); setAdminUser(null); }}>
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1, overflowY:'auto', maxHeight:'100%', display:'flex', flexDirection:'column' }}>
        {/* Top bar */}
        <div style={{ padding:'12px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>
            <span style={{ color:'var(--text-primary)', fontWeight:600 }}>IgnitorAds AdTech Hub</span>
            {' '}<span style={{ opacity:0.5 }}>/</span>{' '}
            <span style={{ color:'var(--cyan)', fontWeight:700 }}>Admin Panel</span>
          </div>
          <AdminNotificationBell onNavigateSupport={() => setActivePage('support')} />
        </div>

        <div style={{ padding:'24px', flex:1 }}>
        {/* Stats bar — always visible */}
        {stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'8px', marginBottom:'20px' }}>
            {[
              { label:'Companies', value: stats.totalCompanies, color:'cyan' },
              { label:'Active',    value: stats.activeCompanies, color:'green' },
              { label:'Suspended', value: stats.suspendedCompanies, color:'amber' },
              { label:'Users',     value: stats.totalUsers, color:'purple' },
              { label:'MRR',       value: fmtUSD(stats.mrr), color:'green' },
            ].map(({ label, value, color }) => (
              <div key={label} className={`stat-card ${color}`}>
                <div className="stat-label">{label}</div>
                <div className={`stat-value ${color}`}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Companies page */}
        {activePage === 'companies' && (
          <div>
            <div style={{ display:'flex', gap:'10px', marginBottom:'16px', alignItems:'center' }}>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:700, flex:1 }}>Companies</h2>
              <input className="input-field" style={{ width:'260px' }} placeholder="Search companies..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
              <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}><RefreshCw size={12}/></button>
              <button className="btn btn-primary" onClick={()=>setShowCreate(true)}><Plus size={13}/> New Company</button>
            </div>
            {loading ? (
              <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
            ) : filtered.length === 0 ? (
              <div className="card" style={{ textAlign:'center', padding:'40px' }}>
                <Building2 size={28} style={{ color:'var(--text-muted)', marginBottom:'12px' }}/>
                <div style={{ color:'var(--text-muted)', fontSize:'13px' }}>No companies yet</div>
              </div>
            ) : (
              filtered.map(c => <CompanyCard key={c.id} company={c} toolPrices={toolPrices} onRefresh={loadData}/>)
            )}
          </div>
        )}

        {activePage === 'pricing'   && <ToolPricingPage />}
        {activePage === 'engine'    && <EngineSettingsPage />}
        {activePage === 'finance'   && <FinancePage />}
        {activePage === 'changelog' && <ChangeLogPage />}
        {activePage === 'admins'     && <AdminUsersPage />}
        {activePage === 'support'    && <SupportTicketsPage />}
        {activePage === 'ai-support' && <AISupportPage />}
        {activePage === 'news'       && <AdminNewsPage />}
        {activePage === 'ctv'        && <CtvDatabasePage />}
        {activePage === 'mobileapps' && <MobileAppsDbPage />}
        {activePage === 'sellersjson' && <SellersJsonDbPage />}
        {activePage === 'theme'      && <ThemePage />}
        </div>{/* end inner padding div */}
      </div>{/* end main content */}

      {showCreate && (
        <CompanyModal toolPrices={toolPrices} onClose={()=>setShowCreate(false)} onSaved={loadData}/>
      )}
    </div>
  );
}

// \u2500\u2500\u2500 Sellers.json DB page \u2500\u2500\u2500
// The registry that maps a supply-partner NAME (as it appears in an ad-server
// report) to the DOMAIN that actually hosts its sellers.json. Those are not the
// same thing — Criteo, Onetag, Index Exchange, Smaato and Verve all host theirs
// somewhere else entirely — so this mapping is maintained here once, at admin
// level, and every company onboarded afterwards inherits it.
function SellersJsonDbPage() {
  const [stats, setStats]   = useState(null);
  const [rows, setRows]     = useState([]);
  const [meta, setMeta]     = useState({ total: 0, page: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage]     = useState(0);
  const [sort, setSort]     = useState('name');
  const [dir, setDir]       = useState('asc');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft]   = useState({});
  const [busy, setBusy]     = useState('');
  const [adding, setAdding] = useState(false);
  const EMPTY_SRC = { name:'', domain:'', sellers_url:'', aliases:'', kind:'supply' };
  const [newSrc, setNewSrc] = useState(EMPTY_SRC);

  const loadStats = async () => {
    try { const { data } = await axios.get('/api/admin/sellers-json/stats', authH()); setStats(data); } catch {}
  };
  const load = async () => {
    try {
      const { data } = await axios.get('/api/admin/sellers-json/sources', {
        ...authH(), params: { search, status, page, limit: 25, sort, dir },
      });
      setRows(data.rows || []); setMeta(data);
    } catch (e) { alert('Could not load sources'); }
  };
  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, status, page, sort, dir]);

  const hdr = (key, label, right) => (
    <th onClick={() => { setPage(0); if (sort === key) setDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSort(key); setDir('asc'); } }}
        style={{ cursor:'pointer', textAlign: right ? 'right' : 'left', whiteSpace:'nowrap' }}>
      {label}{sort === key ? (dir === 'asc' ? ' ^' : ' v') : ''}
    </th>
  );

  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({ domain: r.domain || '', name: r.name || '', sellers_url: r.sellers_url || '',
               kind: r.kind || 'supply', notes: r.notes || '',
               aliases: (Array.isArray(r.aliases) ? r.aliases : []).join(', ') });
  };
  const saveEdit = async (r) => {
    try {
      await axios.put(`/api/admin/sellers-json/sources/${r.id}`, {
        ...draft,
        domain: draft.domain.trim() || null,
        aliases: draft.aliases.split(',').map(s => s.trim()).filter(Boolean),
      }, authH());
      setEditing(null); load(); loadStats();
    } catch (e) { alert(e?.response?.data?.error || 'Save failed'); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name} and all its stored entries?`)) return;
    try { await axios.delete(`/api/admin/sellers-json/sources/${r.id}`, authH()); load(); loadStats(); }
    catch { alert('Delete failed'); }
  };
  // Manual add. Creates the source, then fetches it straight away so a new
  // entity is usable immediately rather than waiting for a later pass.
  const addSource = async () => {
    const name = newSrc.name.trim();
    if (!name) { alert('Entity name is required'); return; }
    let domain = newSrc.domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    // Fallback only: derive from the URL host when the domain is left blank. The
    // domain should be the partner's identity (what appears in ads.txt lines),
    // which is not always the host serving the file — so this is a starting
    // point the admin can correct, not an authority.
    if (!domain && newSrc.sellers_url.trim()) {
      try { domain = new URL(newSrc.sellers_url.trim()).hostname.replace(/^www\./, ''); } catch {}
    }
    setBusy('add');
    try {
      const { data } = await axios.post('/api/admin/sellers-json/sources', {
        name,
        domain: domain || null,
        sellers_url: newSrc.sellers_url.trim() || null,
        aliases: newSrc.aliases.split(',').map(s => s.trim()).filter(Boolean),
        kind: newSrc.kind,
      }, authH());
      setAdding(false);
      setNewSrc(EMPTY_SRC);

      if (data && data.id && domain) {
        setBusy(`fetch-${data.id}`);
        try {
          const { data: f } = await axios.post(`/api/admin/sellers-json/sources/${data.id}/fetch`, {}, authH());
          if (f.skipped)   alert(`${name}: added. File unchanged since the last fetch.`);
          else if (f.ok)   alert(`${name}: added and fetched - ${Number(f.stored).toLocaleString()} publisher entries stored.`);
          else             alert(`${name}: added, but the fetch failed - ${f.error}`);
        } catch (e) {
          alert(`${name}: added, but the fetch failed - ${(e.response && e.response.data && e.response.data.error) || 'request error'}`);
        }
      } else {
        alert(`${name}: added. Map a sellers.json domain before it can be fetched.`);
      }
      load(); loadStats();
    } catch (e) {
      alert((e.response && e.response.data && e.response.data.error) || 'Could not add source');
    } finally { setBusy(''); }
  };

  // Kicks off the same job the weekly cron runs. Returns straight away; the
  // per-source status columns update as it works.
  const refreshAll = async () => {
    if (!window.confirm('Refresh every mapped source? Unchanged files are skipped via ETag, so this is usually quick.')) return;
    setBusy('refresh');
    try {
      await axios.post('/api/admin/sellers-json/refresh-all', {}, authH());
      alert('Refresh started in the background. Reload in a minute to see updated counts and timestamps.');
    } catch (e) {
      alert((e.response && e.response.data && e.response.data.error) || 'Could not start refresh');
    } finally { setBusy(''); }
  };

  const seed = async () => {
    setBusy('seed');
    try {
      const { data } = await axios.post('/api/admin/sellers-json/seed', {}, authH());
      alert(`Seeded ${data.added} new source(s) from the known-SSP map (${data.seedSize} total).`);
      load(); loadStats();
    } catch { alert('Seed failed'); } finally { setBusy(''); }
  };
  // One source at a time on purpose: parsing two large sellers.json concurrently
  // is the realistic way to OOM the API.
  const fetchOne = async (r) => {
    setBusy(`fetch-${r.id}`);
    try {
      const { data } = await axios.post(`/api/admin/sellers-json/sources/${r.id}/fetch`, {}, authH());
      if (data.skipped) alert(`${r.name}: unchanged since last fetch (304) — nothing to do.`);
      else if (data.ok) alert(`${r.name}: stored ${Number(data.stored).toLocaleString()} publisher entries` +
                              (data.removed ? `, removed ${data.removed} stale.` : '.'));
      else alert(`${r.name}: ${data.error}`);
      load(); loadStats();
    } catch (e) { alert(e?.response?.data?.error || 'Fetch failed'); } finally { setBusy(''); }
  };

  const badge = (s) => {
    const map = { ok:'badge-green', error:'badge-red', unmapped:'badge-amber', pending:'badge-muted' };
    return <span className={`badge ${map[s] || 'badge-muted'}`}>{s}</span>;
  };
  const fmt = n => Number(n || 0).toLocaleString();

  return (
    <div>
      <h2 style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
        <Database size={16} style={{ color:'var(--cyan)' }}/> Sellers.json Database
      </h2>
      <p style={{ fontSize:13, color:'var(--text-secondary)', marginTop:0, marginBottom:14, lineHeight:1.6 }}>
        Maps a supply-partner name to the domain hosting its sellers.json. Only PUBLISHER and BOTH entries
        with a usable domain are stored — those are the ones that can be matched against publisher inventory.
        Full per-type counts are kept per source.
      </p>

      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:12, marginBottom:14 }}>
          {[
            { label:'Sources',           value: fmt(stats.sources) },
            { label:'Mapped & fetched',  value: fmt(stats.ok),       color:'var(--green)' },
            { label:'Unmapped',          value: fmt(stats.unmapped), color:'var(--amber)' },
            { label:'Errors',            value: fmt(stats.errored),  color:'var(--red)' },
            { label:'Publisher entries', value: fmt(stats.entries),  sub:`${fmt(stats.distinct_domains)} distinct domains` },
          ].map(st => (
            <div key={st.label} className="card" style={{ padding:'12px 14px' }}>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'4px' }}>{st.label}</div>
              <div style={{ fontSize:'20px', fontWeight:700, color: st.color || 'var(--text-primary)' }}>{st.value}</div>
              {st.sub && <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>{st.sub}</div>}
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
        <input className="input-field" placeholder="Search name, domain or alias..." value={search}
               onChange={e => { setPage(0); setSearch(e.target.value); }} style={{ flex:'1 1 240px' }} />
        <select className="select-field" value={status} onChange={e => { setPage(0); setStatus(e.target.value); }} style={{ width:150 }}>
          <option value="">All statuses</option>
          <option value="ok">Fetched</option>
          <option value="pending">Pending fetch</option>
          <option value="unmapped">Unmapped</option>
          <option value="error">Error</option>
        </select>
        <button className="btn btn-primary" onClick={() => setAdding(a => !a)}>
          {adding ? 'Cancel' : '+ Add source'}
        </button>
        <button className="btn btn-secondary" disabled={busy === 'seed'} onClick={seed}>
          {busy === 'seed' ? 'Seeding...' : 'Seed known SSPs'}
        </button>
        <button className="btn btn-secondary" disabled={busy === 'refresh'} onClick={refreshAll}>
          {busy === 'refresh' ? 'Starting...' : 'Refresh all now'}
        </button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom:12, padding:'14px 16px' }}>
          <div className="card-title" style={{ marginBottom:10 }}>Add a sellers.json source</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={{ fontSize:11, color:'var(--text-muted)' }}>Entity name *</label>
              <input className="input-field w-full" placeholder="PubMatic" value={newSrc.name}
                     onChange={e => setNewSrc(s => ({ ...s, name:e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:'var(--text-muted)' }}>sellers.json domain</label>
              <input className="input-field w-full" placeholder="pubmatic.com" value={newSrc.domain}
                     onChange={e => setNewSrc(s => ({ ...s, domain:e.target.value }))} />
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>
                The partner's own domain - the host that appears in ads.txt lines. Left blank, it is guessed from the URL.
              </div>
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <label style={{ fontSize:11, color:'var(--text-muted)' }}>sellers.json URL</label>
              <input className="input-field w-full" placeholder="https://static.criteo.net/sellers.json" value={newSrc.sellers_url}
                     onChange={e => setNewSrc(s => ({ ...s, sellers_url:e.target.value }))} />
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>
                Only needed when the file is not at https://&#123;domain&#125;/sellers.json - which is common.
              </div>
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <label style={{ fontSize:11, color:'var(--text-muted)' }}>Aliases (comma separated)</label>
              <input className="input-field w-full" placeholder="Pubmatic FI, PubMatic SSP" value={newSrc.aliases}
                     onChange={e => setNewSrc(s => ({ ...s, aliases:e.target.value }))} />
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>
                How this partner appears in ad-server reports. This is what makes name matching work.
              </div>
            </div>
            <div>
              <label style={{ fontSize:11, color:'var(--text-muted)' }}>Kind</label>
              <select className="select-field w-full" value={newSrc.kind}
                      onChange={e => setNewSrc(s => ({ ...s, kind:e.target.value }))}>
                <option value="supply">Supply</option>
                <option value="demand">Demand</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'flex-end', gap:8 }}>
              <button className="btn btn-primary" disabled={busy === 'add' || busy.startsWith('fetch-')} onClick={addSource}>
                {busy === 'add' ? 'Adding...' : busy.startsWith('fetch-') ? 'Fetching...' : 'Add & fetch'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setAdding(false); setNewSrc(EMPTY_SRC); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ overflowX:'auto' }}>
        <table className="data-table">
          <thead><tr>
            {hdr('name','Partner')}
            {hdr('domain','sellers.json domain')}
            {hdr('kind','Kind')}
            {hdr('status','Status')}
            {hdr('entry_count','Publishers stored', true)}
            {hdr('total_sellers','Total sellers', true)}
            {hdr('last_fetched','Last fetched')}
            <th>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={{ fontWeight:600 }}>{r.name}</td>
                  <td style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                    {r.domain || <span style={{ color:'var(--amber)' }}>needs mapping</span>}
                    {r.sellers_url && <div style={{ fontSize:10, color:'var(--text-secondary)' }}>{r.sellers_url}</div>}
                  </td>
                  <td>{r.kind}</td>
                  <td>{badge(r.status)}{r.last_error && <div style={{ fontSize:10, color:'var(--red)' }}>{r.last_error}</div>}</td>
                  <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(r.entry_count)}</td>
                  <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>
                    {fmt(r.total_sellers)}
                    {r.total_sellers > 0 && (
                      <div style={{ fontSize:10, color:'var(--text-secondary)' }}>
                        {fmt(r.publishers)} pub / {fmt(r.intermediaries)} int / {fmt(r.both_count)} both
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize:12 }}>{r.last_fetched ? fmtDateTime(r.last_fetched) : '-'}</td>
                  <td style={{ whiteSpace:'nowrap' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => (editing === r.id ? setEditing(null) : startEdit(r))}>Edit</button>{' '}
                    <button className="btn btn-sm btn-secondary" disabled={!r.domain || busy === `fetch-${r.id}`} onClick={() => fetchOne(r)}>
                      {busy === `fetch-${r.id}` ? 'Fetching...' : 'Fetch'}
                    </button>{' '}
                    <button className="btn btn-sm btn-secondary" onClick={() => del(r)}>Delete</button>
                  </td>
                </tr>
                {editing === r.id && (
                  <tr><td colSpan={8} style={{ background:'var(--bg-elevated)' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, padding:'10px 4px' }}>
                      <div><label style={{ fontSize:11, color:'var(--text-secondary)' }}>Partner name</label>
                        <input className="input-field w-full" value={draft.name} onChange={e => setDraft(d => ({ ...d, name:e.target.value }))} /></div>
                      <div><label style={{ fontSize:11, color:'var(--text-secondary)' }}>sellers.json domain</label>
                        <input className="input-field w-full" placeholder="pubmatic.com" value={draft.domain} onChange={e => setDraft(d => ({ ...d, domain:e.target.value }))} /></div>
                      <div style={{ gridColumn:'1 / -1' }}>
                        <label style={{ fontSize:11, color:'var(--text-secondary)' }}>
                          Aliases (comma separated) - how this partner appears in ad-server reports
                        </label>
                        <input className="input-field w-full" placeholder="Pubmatic FI, PubMatic SSP, pubmatic" value={draft.aliases}
                               onChange={e => setDraft(d => ({ ...d, aliases:e.target.value }))} />
                      </div>
                      <div><label style={{ fontSize:11, color:'var(--text-secondary)' }}>sellers.json URL override</label>
                        <input className="input-field w-full" placeholder="leave blank for https://{domain}/sellers.json" value={draft.sellers_url}
                               onChange={e => setDraft(d => ({ ...d, sellers_url:e.target.value }))} /></div>
                      <div><label style={{ fontSize:11, color:'var(--text-secondary)' }}>Kind</label>
                        <select className="select-field w-full" value={draft.kind} onChange={e => setDraft(d => ({ ...d, kind:e.target.value }))}>
                          <option value="supply">Supply</option><option value="demand">Demand</option><option value="both">Both</option>
                        </select></div>
                      <div style={{ gridColumn:'1 / -1', display:'flex', gap:8 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => saveEdit(r)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ color:'var(--text-secondary)', padding:14 }}>
                No sources yet. Click <strong>Seed known SSPs</strong> for the built-in map, or <strong>+ Add source</strong> to enter one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {meta.pages > 1 && (
        <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:10 }}>
          <button className="btn btn-sm btn-secondary" disabled={page <= 0} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span style={{ fontSize:12, color:'var(--text-secondary)' }}>Page {meta.page + 1} of {meta.pages} - {fmt(meta.total)} sources</span>
          <button className="btn btn-sm btn-secondary" disabled={page + 1 >= meta.pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
